"use client";
import { Profiler, useState, useEffect, useRef, useCallback, useMemo, startTransition } from "react";
import { flushSync } from "react-dom";
import { Tabs, TabsContent } from "../ui/tabs";
import { Card, CardContent } from "../ui/card";
import logo from "../../asset/VirtualScribe_logo.webp";
import WelcomeVideoPlayer from './WelcomeVideoPlayer';
import {
  FileText,
  Mic,
  Upload,
  User,
  ClipboardList,
  X,
  Lock,
  Edit3,
  Loader2,
  Menu,
  Stethoscope,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Copy,
  Play,
  Zap,
  Check,
  Sparkles,
  Clock,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-hot-toast";
// Components
import SessionHeader from "./session-header";
import SessionManager from "./session-manager";
import GrammarCheckPanel from "./grammar-check-panel";
import { TranscriptionInterface } from "./transcription-interface";
import AudioUploadTab from "./audio-upload-tab";
import RecordingOptions from "./recording-options";
import PatientContextForm from "./patient-context-form";
import EnhancedSoapNoteDisplay from "./EnhancedSoapNoteDisplay";
import TrialExhaustedModal from "../ui/trial-exhausted-modal";
import PrechartingTab from "./precharting/pre-charting-tab";
import SoapGenerationFailedModal from "./SoapGenerationFailedModal";
import { Button } from "../ui/button";
import {
  SOAP_GENERATION_FAILED_AGAIN_BODY,
  SOAP_GENERATION_FAILED_BODY,
  SOAP_GENERATION_FAILED_TITLE,
} from "../../utils/soapGenerationFailure";
// Demo visit (onboarding walkthrough)
import DemoTour, { TOUR_STEP } from "../user-tour/DemoTour";
import { DEMO_PATIENT_CONTEXT, DEMO_TRANSCRIPT_LINES, DEMO_SOAP_NOTE,DEMO_ICD_HELPER } from "../user-tour/demoData";

// Hooks
import {
  useRecording,
  useRecordingLiveUi,
  acquireDisplayMediaStream,
  needsDisplayMediaCapture,
  needsMicCapture,
  stopMediaStream,
} from "../../hooks/useRecording";
import { useAudioPlayback } from "../../hooks/useAudioPlayback";
import { useGrammarCheck } from "../../hooks/useGrammarCheck";
import { useTrial } from "../../context/TrialContext";
import { useAuth } from "../../context/AuthContext";
import { useUserRole } from "../../hooks/useUserRole";
import {
  useDemoTour,
  waitForTourTarget,
  hasCompletedProductTour,
  markProductTourCompleted,
} from "../../hooks/useDemoTour";

// Services
import { aiService } from "../../apis/aiService";
import { resolveLiveStreamingLocale } from "../../utils/liveTranscribeLocale";
import { formatTranscriptLines } from "../../utils/formatTranscriptLines";
import {
  canFastRestoreFromCache,
  findCachedSession,
  getStoredActiveSessionId,
  readSessionsListCache,
  removeSessionFromListCache,
  sortSessionsNewestFirst,
} from "../../utils/sessionsListCache";
import {
  loadDemoSnapshot,
  resolveAuthUserId,
  resolveDemoVisitSelection,
  saveDemoSnapshot,
  shouldShowDemoVisitInSessionList,
} from "../../utils/demoVisitStore";
import { sessionService } from "../../apis/sessionService";
import { audioService } from "../../apis/audioService";
import { ensureWebSocketReady } from "../../services/websocketService";
import { applyLocalSessionProgress, getProgressForSession, subscribeTranscriptionJobProgress, toUiProgress } from "../../utils/transcriptionJobProgressStore";
import { useSessionTranscriptionProgress } from "../../hooks/useTranscriptionJobProgress";
import { v4 as uuidv4 } from "uuid";
import { useAudioCache } from "../../hooks/useAudioCache";
import MaintenanceModal from "../ui/maintenance-modal";
import { getScrapeProgress, getMyProfile } from "../../apis/userApiService";
import { DottedLoader } from "../ui/loading-spinner";
import { ACTIVE_TEMPLATE_CHANGED_EVENT, notifyTemplateRegenStatus } from "../ActiveTemplateIndicator";
import { cleanSessionName } from '../../utils/sessionUtils';
import {
  cleanupStaleUiOverlays,
  consumePostPaymentScribeRefresh,
} from '../../utils/postPaymentNavigation';
import {
  setParkedCapture,
  getParkedCapture,
  clearParkedCapture,
  hasParkedCapture,
  listParkedCaptureSessionIds,
  subscribeParkedCaptures,
  getParkedCaptureMeta,
  markCaptureFinished,
  clearCaptureFinished,
  isCaptureFinished,
} from "../../utils/parkedCaptureStore";
// Import STATUS from react-joyride
import { STATUS } from "react-joyride";

/** Live STT text — subscribed here so MedicalScribeApp is not re-rendered every partial. */
function LiveTranscriptionPanelBody({
  recording,
  grammarCheck,
  uploadTranscriptionStream,
  isTranscriptionCollapsed,
}) {
  const liveUi = useRecordingLiveUi(recording);
  return (
    <div className={isTranscriptionCollapsed ? "hidden" : "p-4 space-y-4"}>
      {grammarCheck.showGrammarPanel && (
        <div className="animate-in slide-in-from-top duration-100">
          <GrammarCheckPanel
            suggestions={grammarCheck.grammarSuggestions}
            onApplySuggestion={(suggestion) =>
              grammarCheck.applySuggestion(
                suggestion,
                liveUi.transcript,
                recording.setTranscript,
              )
            }
            onApplyAll={() =>
              grammarCheck.applyAllCorrections(recording.setTranscript)
            }
          />
        </div>
      )}
      <TranscriptionInterface
        transcript={liveUi.transcript}
        liveTranscript={liveUi.liveTranscript}
        transcriptionSource={recording.transcriptionSource}
        correctedTranscript={grammarCheck.correctedTranscript}
        isRecording={recording.isRecording || recording.isParked}
        isPaused={recording.isPaused || recording.isParked}
        isGrammarChecking={grammarCheck.isGrammarChecking}
        uploadStreamActive={uploadTranscriptionStream.active}
        uploadStreamLines={uploadTranscriptionStream.lines}
        uploadStreamStatus={uploadTranscriptionStream.status}
      />
    </div>
  );
}

function SessionHeaderWithLiveClock({ recording, ...headerProps }) {
  const liveUi = useRecordingLiveUi(recording);
  return (
    <SessionHeader {...headerProps} recordingTime={liveUi.recordingTime} />
  );
}

/**
 * Owns WS progress subscription so MedicalScribeApp / SessionManager do not
 * re-render on every percent tick while a job is running.
 */
function SoapNotePlaceholderCard({
  sessionId,
  isDemoMode,
  demoBackgroundProgress,
  isAssignedSOAPNote,
  soapGenerationFailed,
  soapGenerationFailedAgain,
  isGeneratingSoap,
  onRegenerateSoapNote,
}) {
  const sessionJobProgress = useSessionTranscriptionProgress(sessionId);
  const backgroundProgress = isDemoMode
    ? demoBackgroundProgress
    : sessionJobProgress;

  if (backgroundProgress?.active && !isAssignedSOAPNote) {
    const percent = Math.min(
      100,
      Math.max(0, Math.round(Number(backgroundProgress.percent) || 0)),
    );
    return (
      <div className="max-w-2xl mx-auto text-left">
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-slate-800">
            Generating SOAP note sections
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Building{" "}
            <span className="font-medium">Subjective</span>,{" "}
            <span className="font-medium">Objective</span>,{" "}
            <span className="font-medium">Assessment</span>, and{" "}
            <span className="font-medium">Plan</span> from your conversation.
          </p>
        </div>
        <div className="mb-2 flex items-center justify-between text-sm text-slate-600">
          <span className="font-medium">
            {backgroundProgress.message || "Working on transcription..."}
          </span>
          <span className="tabular-nums font-semibold text-slate-700">
            {percent}%
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-[width] duration-200"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="mt-4 text-sm text-slate-500">
          Stay on this SOAP Note tab. Your note will appear automatically when
          processing completes.
        </p>
        <div className="space-y-5 mb-6">
          {["Subjective", "Objective", "Assessment", "Plan"].map((section) => (
            <div
              key={section}
              className="rounded-lg border border-slate-200 bg-white p-5 min-h-[140px]"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="h-5 w-36 bg-slate-200 rounded animate-pulse"></div>
                <span className="text-xs text-slate-500 font-medium">
                  {section}
                </span>
              </div>
              <div className="space-y-3">
                <div className="h-3.5 w-full bg-slate-100 rounded animate-pulse"></div>
                <div className="h-3.5 w-11/12 bg-slate-100 rounded animate-pulse"></div>
                <div className="h-3.5 w-5/6 bg-slate-100 rounded animate-pulse"></div>
                <div className="h-3.5 w-2/3 bg-slate-100 rounded animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center ${
          soapGenerationFailed
            ? "bg-red-50"
            : "bg-gradient-to-br from-slate-100 to-slate-200"
        }`}
      >
        {soapGenerationFailed ? (
          <AlertCircle size={32} className="text-red-500" />
        ) : (
          <FileText size={32} className="text-slate-400" />
        )}
      </div>
      <h3 className="text-xl font-semibold text-slate-700 mb-2">
        {isAssignedSOAPNote
          ? "Assigned SOAP Note"
          : soapGenerationFailed
            ? SOAP_GENERATION_FAILED_TITLE
            : "No SOAP note generated yet"}
      </h3>
      <p className="text-slate-500 mb-6 max-w-md mx-auto">
        {isAssignedSOAPNote
          ? "You have been assigned to complete this SOAP note. Click the edit button to start."
          : soapGenerationFailed
            ? soapGenerationFailedAgain
              ? SOAP_GENERATION_FAILED_AGAIN_BODY
              : SOAP_GENERATION_FAILED_BODY
            : "Fill patient details, then upload recorded audio or start live visit. Your SOAP note will appear automatically."}
      </p>
      {soapGenerationFailed && !isAssignedSOAPNote ? (
        <Button
          type="button"
          onClick={onRegenerateSoapNote}
          disabled={isGeneratingSoap}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <RefreshCw className={isGeneratingSoap ? "animate-spin" : ""} />
          {isGeneratingSoap ? "Regenerating…" : "Regenerate SOAP Note"}
        </Button>
      ) : (
        <div className="flex items-center justify-center gap-4 text-sm text-slate-400 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
            Patient Info
          </div>
          <div className="w-1 h-1 bg-slate-300 rounded-full"></div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            Audio capture
          </div>
          <div className="w-1 h-1 bg-slate-300 rounded-full"></div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
            SOAP (auto)
          </div>
        </div>
      )}
    </>
  );
}

export default function MedicalScribeApp() {
  const { sessionId: pathSessionId } = useParams();
  const onRenderProfiler = useCallback((id, phase, actualDuration) => {
    if (process.env.NODE_ENV !== "development") return;
    if (actualDuration > 12) {
      console.debug(`[Profiler] ${id} ${phase}: ${actualDuration.toFixed(2)}ms`);
    }
  }, []);

  const { isAdmin } = useUserRole();
  const { currentUser } = useAuth();
  const authToken =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const activeSessionRef = useRef(activeSession);
  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);
  const [activeSessionName, setActiveSessionName] = useState("");
  // Always start loading so the main pane never flashes blank before hydrate.
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  /** Gates welcome-modal logic until the first sessions hydrate finishes. */
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  /** Soft cross-fade while switching between visits (not the full-page skeleton). */
  const [isSwitchingSession, setIsSwitchingSession] = useState(false);
  /** Visits are still loading/hydrating — uploads must wait so audio can't attach to the wrong/unready visit. */
  const isSessionDataLoading = isLoadingSessions || isSwitchingSession;
  const [lastSessionLoadTime, setLastSessionLoadTime] = useState(0);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [sessionsVersion, setSessionsVersion] = useState(0);
  const [sessionListPatch, setSessionListPatch] = useState(null);
  const sessionPatchNonceRef = useRef(0);
  
  const emitSessionPatch = useCallback((patch) => {
  sessionPatchNonceRef.current += 1;
  setSessionListPatch({ ...patch, nonce: sessionPatchNonceRef.current });
}, []);
 /** Latest generateSoapNote for timeouts (stop live / upload complete). */
  const generateSoapNoteRef = useRef(async () => { });
  /** Transcript + id from the last SOAP attempt so retry does not restart the visit. */
  const lastSoapAttemptRef = useRef({
    transcriptionText: "",
    transcriptionId: "",
  });
  /** Live-recording SOAP jobs queued like uploads — jobId → sessionId. */
  const backgroundSoapJobIdsRef = useRef(new Map());
  /** Session currently stopping live capture to enqueue SOAP — do not re-park it. */
  const liveStopSessionIdRef = useRef(null);
  const soapGenerationFailedRef = useRef(false);

  const sessionCreationLock = useRef(false);
  /** Dismisses stale getSession() results if the user creates/switches session before a request finishes. */
  const intendedSessionIdRef = useRef(null);
  const inFlightSelectSessionRef = useRef(null);
  /** Next transcript_seg index per sessionId for temp-audio persistence. */
  const transcriptSegmentIndexRef = useRef({});
  /** Next audio temp-chunk index per sessionId (pause/park segments). */
  const audioChunkIndexRef = useRef({});
  /** How many in-memory audio segments already uploaded as temp chunks, per session. */
  const uploadedAudioSegmentCountRef = useRef({});
  const lastSelectSessionRef = useRef({ id: null, ts: 0 });
  const hasInitialSessionCreated = useRef(false);
  const hasLoadedInitialDataRef = useRef(false);
  const newVisitButtonRef = useRef(null);
  const mainContentColumnRef = useRef(null);
  const toastShownRef = useRef({});
  const initialUrlSessionIdRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Capture sessionId from URL once on mount so reload always restores the same session
  if (initialUrlSessionIdRef.current === null && location.pathname === "/scribe") {
    const params = new URLSearchParams(location.search);
    const sid = params.get("sessionId");
    if (sid && sid !== "undefined" && sid !== "null") {
      initialUrlSessionIdRef.current = sid;
    }
  }

  // App State
  const [isAssignedSOAPNote, setIsAssignedSOAPNote] = useState(false);
  const [initialTab, setInitialTab] = useState("patient");
  const [activeTab, setActiveTab] = useState("patient");
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingSoap, setIsGeneratingSoap] = useState(false);
  const [showSoapGenerationFailedModal, setShowSoapGenerationFailedModal] =
    useState(false);
  const [soapGenerationFailed, setSoapGenerationFailed] = useState(false);
  const [soapGenerationFailedAgain, setSoapGenerationFailedAgain] =
    useState(false);
  const [soapNote, setSoapNote] = useState(null);
  const [patientContext, setPatientContext] = useState({});
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [awaitingVisitSelection, setAwaitingVisitSelection] = useState(false);
  const [transcriptionId, setTranscriptionId] = useState("");
  const [pendingTabChange, setPendingTabChange] = useState(null);
  const [liveTranscribed, setLiveTranscribed] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [showPrechartingSection, setShowPrechartingSection] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  /** WebSocket lines from an audio upload job — shown in Transcription tab with the same prompter as live Azure. */
  const [uploadTranscriptionStream, setUploadTranscriptionStream] = useState({
    active: false,
    lines: [],
    status: "",
  });
  const shownCompletionToastKeysRef = useRef(new Set());
  const lastLiveAudioFileRef = useRef(null);
  const [demoBackgroundProgress, setDemoBackgroundProgress] = useState({
    active: false,
    stage: "",
    percent: 0,
    message: "",
  });
  const [showSoapRevealEffect, setShowSoapRevealEffect] = useState(false);
  const [soapTypingAnimationNonce, setSoapTypingAnimationNonce] = useState(0);
  const shouldAnimateNextSoapRef = useRef(false);
  const soapTypingAnimationEnabledRef = useRef(false);
  const [showTranscriptionInSoapTab, setShowTranscriptionInSoapTab] = useState(false);
  const [isTranscriptionCollapsed, setIsTranscriptionCollapsed] = useState(false);
  const [audioSourceMode, setAudioSourceMode] = useState('input');
  const [audioInputDeviceId, setAudioInputDeviceId] = useState('');
  // Per-chart HPI depth (1=Concise, 2=Balanced, 3=Detailed). Defaults to the
  // account-level preference; users can override it for an individual chart.
  const [accountHpiLevel, setAccountHpiLevel] = useState(3);
  const [chartHpiLevel, setChartHpiLevel] = useState(3);
  // True while the HPI/Subjective is being regenerated at a new depth level.
  const [hpiRegenerating, setHpiRegenerating] = useState(false);
  // True while regenerating/swapping SOAP for a template switch (section skeletons, like Magic Editor).
  const [templateRegenerating, setTemplateRegenerating] = useState(false);
  const [templateRegenLabel, setTemplateRegenLabel] = useState("");
  const previousSoapNoteRef = useRef(null);
  const soapNoteRef = useRef(null);
  const templateRegenInFlightRef = useRef(false);
  const templateRegenRequestIdRef = useRef(0);
  const templateRegenTimeoutRef = useRef(null);
  const [scrollToTranscriptionNonce, setScrollToTranscriptionNonce] = useState(0);
  const soapScrollRef = useRef(null);
  //Header
  const [isMobile, setIsMobile] = useState(
  typeof window !== "undefined" ? window.innerWidth < 768 : false
);
  // ----- Demo visit (onboarding walkthrough) state — new -----
  const [welcomeStep, setWelcomeStep] = useState(1);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [tourKey, setTourKey] = useState(0);
  const [keepTourPanelOpen, setKeepTourPanelOpen] = useState(false);
  const [tourShowSkip, setTourShowSkip] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [tourTemplateOpen, setTourTemplateOpen] = useState(false);
  const [tourRecordingOpen, setTourRecordingOpen] = useState(false);
  const [demoAnimationNonce, setDemoAnimationNonce] = useState(0);
  const [activeTourStep, setActiveTourStep] = useState(null);
  const demoTour = useDemoTour();
  const demoIntervalRef = useRef(null);
  const isPausedRef = useRef(false);
  const [showDemoComplete, setShowDemoComplete] = useState(false);
  const [micDeniedMessage, setMicDeniedMessage] = useState(false);
  // Track whether the tour has been started for the current demo session
  const demoTourStartedRef = useRef(false);
  const productTourLaunchRef = useRef(0);
  const launchProductTourRef = useRef(null);
  const runTourRef = useRef(false);
  const productTourSeenRef = useRef(false);
  const [demoVisitSnapshot, setDemoVisitSnapshot] = useState(null);
  const [isViewingDemoSnapshot, setIsViewingDemoSnapshot] = useState(false);
  const isViewingDemoSnapshotRef = useRef(false);
  const demoVisitSnapshotRef = useRef(null);
  const hasAutoStartedDemoRef = useRef(false);
  const tourStepIndexRef = useRef(0);
  // Set when Next on tour Step 5 triggers the demo capture, so the transcript
  // loop auto-stops and generates the SOAP note without a manual Stop click.
  const autoStopDemoRef = useRef(false);
  // Ref mirrors of values the Joyride callback / demo interval need
  // synchronously (they are memoized / setInterval closures).
  const demoRecordingActiveRef = useRef(false);
  const runAutoDemoCaptureRef = useRef(null);

  // Keep tourStepIndexRef in lockstep with the tourStepIndex state so
  useEffect(() => {
    tourStepIndexRef.current = tourStepIndex;
  }, [tourStepIndex]);

  useEffect(() => {
    isViewingDemoSnapshotRef.current = isViewingDemoSnapshot;
  }, [isViewingDemoSnapshot]);

  useEffect(() => {
    demoVisitSnapshotRef.current = demoVisitSnapshot;
  }, [demoVisitSnapshot]);

  useEffect(() => {
  const handleResize = () => setIsMobile(window.innerWidth < 768);
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);
  // Restore this user's demo snapshot only. Admin All Visits must never
  // inherit another account's local demo, and switching users swaps state.
  useEffect(() => {
    const userId = resolveAuthUserId(currentUser);
    if (!userId) return;

    if (isAdmin) {
      demoVisitSnapshotRef.current = null;
      setDemoVisitSnapshot(null);
      if (isViewingDemoSnapshotRef.current || activeSessionRef.current === "demo-session") {
        isViewingDemoSnapshotRef.current = false;
        setIsViewingDemoSnapshot(false);
        setIsDemoMode(false);
        if (intendedSessionIdRef.current === "demo-session") {
          intendedSessionIdRef.current = null;
        }
        if (activeSessionRef.current === "demo-session") {
          activeSessionRef.current = null;
          setActiveSession(null);
          if (localStorage.getItem("activeSessionId") === "demo-session") {
            localStorage.removeItem("activeSessionId");
          }
        }
      }
      return;
    }

    const snapshot = loadDemoSnapshot(userId, { isAdmin: false });
    demoVisitSnapshotRef.current = snapshot;
    setDemoVisitSnapshot(snapshot);
  }, [currentUser, isAdmin]);

  useEffect(() => {
    soapNoteRef.current = soapNote;
    if (soapNote) {
      soapGenerationFailedRef.current = false;
      setSoapGenerationFailed(false);
      setSoapGenerationFailedAgain(false);
      setShowSoapGenerationFailedModal(false);
    }
  }, [soapNote]);

  useEffect(() => {
    return () => {
      if (templateRegenTimeoutRef.current) {
        clearTimeout(templateRegenTimeoutRef.current);
      }
    };
  }, []);

  // Clean up the demo transcript interval on unmount so it never keeps
  // ticking (and calling setState) after the component is gone.
  useEffect(() => {
    return () => {
      if (demoIntervalRef.current) {
        clearInterval(demoIntervalRef.current);
        demoIntervalRef.current = null;
      }
    };
  }, []);

  runTourRef.current = demoTour.runTour;

  // When the guided tour finishes (or is skipped), show the completion modal.
  useEffect(() => {
    if (demoTour.tourFinished && isDemoMode) {
      setShowDemoComplete(true);
    }
  }, [demoTour.tourFinished, isDemoMode]);

  // Custom Hooks – must be defined BEFORE any effect that uses them
  const recording = useRecording();

  const [parkedSessionIds, setParkedSessionIds] = useState(() =>
    listParkedCaptureSessionIds()
  );
  const [isResumingCapture, setIsResumingCapture] = useState(false);
  const [isParkingCapture, setIsParkingCapture] = useState(false);
  /** Per-session parked persist so visit B is not blocked/aborted by visit A. */
  const persistJobsRef = useRef(new Map());
  /** Bumped to abort in-flight park persist after Stop/SOAP so leftover temp cannot re-park. */
  const parkedPersistEpochRef = useRef(new Map());

  const invalidateParkedPersist = useCallback((sessionId) => {
    const sid = sessionId ? String(sessionId) : null;
    if (!sid) return;
    const next = (parkedPersistEpochRef.current.get(sid) || 0) + 1;
    parkedPersistEpochRef.current.set(sid, next);
  }, []);

  useEffect(() => {
    return subscribeParkedCaptures((ids) => {
      setParkedSessionIds(ids);
    });
  }, []);

  const buildLiveCaptureOptions = useCallback(() => {
    const streamingFlag = process.env.REACT_APP_ENABLE_STREAMING_TRANSCRIPTION;
    const serverLiveSttEnabled =
      streamingFlag == null || String(streamingFlag).trim() === ""
        ? true
        : String(streamingFlag).toLowerCase() === "true";
    const streamingUserId =
      currentUser?.userId ?? currentUser?.userid ?? currentUser?.id ?? null;
    return {
      audioSourceMode,
      audioInputDeviceId: audioInputDeviceId || undefined,
      ...(serverLiveSttEnabled && streamingUserId
        ? {
            azureStreaming: {
              userId: String(streamingUserId),
              token: authToken || "",
            },
          }
        : {}),
    };
  }, [audioSourceMode, audioInputDeviceId, currentUser, authToken]);

  /**
   * Persist parked transcript/audio off the critical UI path (background).
   * Parallel uploads; failures are logged and retried on next park/stop.
   */
  const persistParkedCaptureInBackground = useCallback((sessionId, snapshot) => {
    const sid = sessionId ? String(sessionId) : null;
    if (!sid || !snapshot) return;

    const epoch = parkedPersistEpochRef.current.get(sid) || 0;
    const isStale = () => (parkedPersistEpochRef.current.get(sid) || 0) !== epoch;

    const run = async () => {
      if (isStale()) return;
      const fullText = String(snapshot.transcript || "").trim();
      if (fullText) {
        try {
          const idx = transcriptSegmentIndexRef.current[sid] ?? 0;
          await audioService.saveTempTranscriptSegment(sid, {
            text: fullText,
            fullText,
            segmentIndex: idx,
          });
          if (isStale()) return;
          transcriptSegmentIndexRef.current[sid] = idx + 1;
        } catch (err) {
          console.warn("Background transcript persist failed:", err?.message || err);
        }
      }

      const segments = Array.isArray(snapshot.audioSegments)
        ? snapshot.audioSegments
        : [];
      const alreadyUploaded = uploadedAudioSegmentCountRef.current[sid] ?? 0;
      let nextChunkIdx = audioChunkIndexRef.current[sid] ?? alreadyUploaded;
      for (let i = alreadyUploaded; i < segments.length; i++) {
        if (isStale()) return;
        const blob = segments[i];
        if (!blob || !blob.size) continue;
        const chunkIdx = nextChunkIdx;
        const mime = blob.type || "audio/webm";
        const ext = mime.includes("webm") ? "webm" : "wav";
        const file = new File([blob], `live_seg_${chunkIdx}.${ext}`, { type: mime });
        try {
          await audioService.uploadTempAudioChunk(sid, file, chunkIdx, { isFinal: false });
          if (isStale()) return;
          nextChunkIdx = chunkIdx + 1;
          audioChunkIndexRef.current[sid] = nextChunkIdx;
          uploadedAudioSegmentCountRef.current[sid] = i + 1;
        } catch (err) {
          console.warn("Background audio chunk persist failed:", err?.message || err);
        }
      }
    };

    const prev = persistJobsRef.current.get(sid) || Promise.resolve();
    const job = prev.then(run, run);
    persistJobsRef.current.set(sid, job);
  }, []);

  const waitForParkedPersist = useCallback(async (sessionId) => {
    const sid = sessionId ? String(sessionId) : null;
    if (!sid) return;
    const job = persistJobsRef.current.get(sid);
    if (job) {
      await job.catch(() => {});
    }
  }, []);

  const releaseParkedAfterSoap = useCallback(
    (sessionId, { endActiveParked = true } = {}) => {
      const sid = sessionId ? String(sessionId) : null;
      if (!sid) return;
      invalidateParkedPersist(sid);
      markCaptureFinished(sid);
      clearParkedCapture(sid);
      delete transcriptSegmentIndexRef.current[sid];
      delete audioChunkIndexRef.current[sid];
      delete uploadedAudioSegmentCountRef.current[sid];
      const active =
        activeSessionRef.current != null ? String(activeSessionRef.current) : "";
      if (endActiveParked && active === sid) {
        recording.endParkedCapture();
      }
    },
    [invalidateParkedPersist, recording]
  );

  /**
   * Fast critical-path park: release hardware + snapshot locally.
   * Network durability runs in background so patient switch stays snappy.
   */
  const parkCurrentCaptureIfNeeded = useCallback(async (sessionId, { autoPause = false } = {}) => {
    const sid = sessionId ? String(sessionId) : null;
    if (!sid) return false;
    if (isCaptureFinished(sid)) {
      recording.endParkedCapture();
      clearParkedCapture(sid);
      return false;
    }

    if (recording.isRecording && !recording.isPaused && autoPause) {
      recording.pauseRecording();
    }

    const generationAtPark =
      typeof recording.getCaptureGeneration === "function"
        ? recording.getCaptureGeneration()
        : null;
    let snapshot = null;
    if (recording.isRecording) {
      if (!recording.isPaused) {
        recording.pauseRecording();
      }
      snapshot = await recording.parkRecording();
    } else if (recording.isParked) {
      snapshot = recording.getParkedSnapshot();
    } else {
      return false;
    }

    if (!snapshot) return false;

    setParkedCapture(sid, {
      ...snapshot,
      liveTranscribed: true,
      mode: "transcribing",
    });
    persistParkedCaptureInBackground(sid, snapshot);
    const generationNow =
      typeof recording.getCaptureGeneration === "function"
        ? recording.getCaptureGeneration()
        : generationAtPark;
    if (generationAtPark == null || generationNow === generationAtPark) {
      recording.clearRecording();
    }
    setParkedSessionIds(listParkedCaptureSessionIds());
    setSessionsVersion((v) => v + 1);
    return true;
  }, [recording, persistParkedCaptureInBackground]);

  const restoreParkedCaptureForSession = useCallback(async (sessionId, { hasSoapNote = false } = {}) => {
    const sid = sessionId ? String(sessionId) : null;
    if (!sid) return false;

    // SOAP already exists: parked leftover (memory, sessionStorage, or temp transcript)
    // is from the completed capture. New live recording is blocked once a note exists.
    if (hasSoapNote || isCaptureFinished(sid)) {
      releaseParkedAfterSoap(sid);
      return false;
    }

    const parked = getParkedCapture(sid);
    if (parked) {
      recording.applyParkedSnapshot(parked);
      setLiveTranscribed(parked.liveTranscribed !== false);
      setMode(parked.mode || "transcribing");
      setShowTranscriptionInSoapTab(true);
      setActiveTab("soap-note");
      return "memory";
    }

    // Recover from Redis/Postgres + sessionStorage meta if in-memory park was lost.
    try {
      const meta = getParkedCaptureMeta(sid);
      const res = await audioService.getTempTranscript(sid);
      const text = String(res?.data?.transcriptText || meta?.transcriptPreview || "").trim();
      if (!text) return false;
      const segmentCount = Number(res?.data?.transcriptSegmentCount) || 0;
      transcriptSegmentIndexRef.current[sid] = segmentCount;

      try {
        const audioState = await audioService.getTempAudioState(sid);
        const chunkCount = Number(audioState?.data?.chunkCount) || 0;
        if (chunkCount > 0) {
          audioChunkIndexRef.current[sid] = chunkCount;
          uploadedAudioSegmentCountRef.current[sid] = chunkCount;
        }
      } catch (_) { /* optional */ }

      recording.applyParkedSnapshot({
        transcript: text,
        audioSegments: [],
        elapsedSeconds: meta?.elapsedSeconds || 0,
        lang: meta?.lang || undefined,
        startOptions: meta?.startOptions
          ? {
              audioSourceMode: meta.startOptions.audioSourceMode || "input",
              azureStreaming: null,
            }
          : null,
      });
      setLiveTranscribed(meta?.liveTranscribed !== false);
      setMode(meta?.mode || "transcribing");
      setShowTranscriptionInSoapTab(true);
      setActiveTab("soap-note");
      setParkedCapture(sid, {
        transcript: text,
        audioSegments: [],
        elapsedSeconds: meta?.elapsedSeconds || 0,
        liveTranscribed: meta?.liveTranscribed !== false,
        mode: meta?.mode || "transcribing",
      });
      return "temp";
    } catch (err) {
      console.warn("Failed to restore temp transcript:", err?.message || err);
      return false;
    }
  }, [recording, releaseParkedAfterSoap]);

  // Auto-start tour on recording start — we now start the tour immediately in handleStartDemo.
  // The tour is now started manually when the user clicks "Start a demo visit".

  // Keep a ref of the recording's paused state so the demo transcript ticker
  // (a setInterval, not a React re-render loop) can check it synchronously.
  useEffect(() => {
    isPausedRef.current = recording?.isPaused || recording?.isParked || false;
    demoRecordingActiveRef.current = Boolean(recording?.isRecording);
  });

  /** Warn before leaving mid-recording (recording state is not persisted across refresh). */
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!recording.isRecording && !recording.isParked && !hasParkedCapture(activeSession)) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [recording.isRecording, recording.isParked, activeSession]);
  const { flushLiveTranscriptFromPending } = recording;
  useEffect(() => {
    flushLiveTranscriptFromPending();
  }, [activeTab, flushLiveTranscriptFromPending]);
  const audioPlayback = useAudioPlayback();
  const grammarCheck = useGrammarCheck();
  const { getPlayableAudioUrl, getAudioForSession, triggerAudioFetch, registerSessionAudio, isCacheValid } = useAudioCache();

  /** Apply API session transcript/corrected fields, or explicitly clear when absent (avoids stale UI from the previous visit). */
  const onUploadTranscriptionStreamState = useCallback((payload) => {
    if (!payload) return;
    setUploadTranscriptionStream({
      active: !!payload.active,
      lines: Array.isArray(payload.lines) ? payload.lines : [],
      status: typeof payload.status === "string" ? payload.status : "",
    });
  }, []);

  const onUploadTranscriptionStart = useCallback(() => {
    setActiveTab("soap-note");
  }, []);

  useEffect(() => {
    const sid =
      activeSession != null &&
      String(activeSession) !== "undefined" &&
      String(activeSession) !== "null"
        ? String(activeSession)
        : "";
    if (!sid) return undefined;

    let wasActive = Boolean(toUiProgress(getProgressForSession(sid)).active);

    const runTabSwitch = () => {
      if (
        liveStopSessionIdRef.current &&
        String(liveStopSessionIdRef.current) !== String(activeSession)
      ) {
        return;
      }
      setActiveTab("soap-note");
      if (soapTypingAnimationEnabledRef.current) {
        shouldAnimateNextSoapRef.current = true;
      }
      let cancelled = false;
      (async () => {
        try {
          const me = await getMyProfile();
          if (cancelled) return;
          const enabled = Boolean(me?.data?.profile?.soapTypingAnimationEnabled);
          soapTypingAnimationEnabledRef.current = enabled;
          shouldAnimateNextSoapRef.current = enabled;
        } catch {
          // Keep last known preference.
        }
      })();
      return () => {
        cancelled = true;
      };
    };

    let cancelProfileFetch = null;
    if (wasActive) {
      cancelProfileFetch = runTabSwitch();
    }

    const unsubscribe = subscribeTranscriptionJobProgress((map) => {
      const active = Boolean(toUiProgress(map[sid] || null).active);
      if (!active) {
        wasActive = false;
        return;
      }
      if (wasActive) return;
      wasActive = true;
      if (typeof cancelProfileFetch === "function") cancelProfileFetch();
      cancelProfileFetch = runTabSwitch();
    });

    return () => {
      unsubscribe();
      if (typeof cancelProfileFetch === "function") cancelProfileFetch();
    };
  }, [activeSession]);

  useEffect(() => {
    const soapSid = soapNote?.sessionId || soapNote?.session_id;
    if (
      soapSid &&
      activeSession &&
      String(soapSid) !== String(activeSession)
    ) {
      return;
    }
    if (recording.isRecording || recording.isParked) return;
    if (!soapNote?.metadata?.transcript) return;
    const transcript = soapNote.metadata.transcript;
    if (typeof transcript === "string" && transcript.trim()) {
      const currentTranscript = recording.transcript || "";
      const newHasLabels = /Speaker\s+\d+:/.test(transcript);
      const currentHasLabels = /Speaker\s+\d+:/.test(currentTranscript);

      // Only overwrite if the new text has labels, or current doesn't have them.
      if (newHasLabels || !currentHasLabels) {
        const formatted = formatTranscriptLines(transcript);
        recording.setTranscript(formatted);
        if (typeof recording.setFullTranscriptText === "function") {
          recording.setFullTranscriptText(formatted);
        }
      } else {
        console.warn("🔍 SOAP metadata transcript is plain – preserving existing labelled transcript.");
      }
      // Mount the panel for any transcript, including ones without speaker labels.
      setShowTranscriptionInSoapTab(true);
    }
  }, [soapNote, recording.isRecording, recording.isParked, activeSession, setShowTranscriptionInSoapTab]);

  // Show the transcript with a newly generated SOAP note instead of collapsing it.
  useEffect(() => {
    const hadSoapBefore = !!previousSoapNoteRef.current;
    const hasSoapNow = !!soapNote;
    if (!hadSoapBefore && hasSoapNow) {
      if (shouldAnimateNextSoapRef.current) {
        setSoapTypingAnimationNonce((prev) => prev + 1);
      }
      // Keep the transcript visible with the new note. Collapsing it hid the
      // text until the user clicked Transcription → Ready at the top.
      const metaTranscript =
        typeof soapNote?.metadata?.transcript === "string"
          ? soapNote.metadata.transcript.trim()
          : "";
      const liveText =
        recording.getLiveUiSnapshot?.()?.transcript?.trim() ||
        recording.getLiveUiSnapshot?.()?.liveTranscript?.trim() ||
        grammarCheck.correctedTranscript?.trim() ||
        "";
      if (isDemoMode || metaTranscript || liveText) {
        setShowTranscriptionInSoapTab(true);
        setIsTranscriptionCollapsed(false);
      }
      setShowSoapRevealEffect(true);
      const timer = setTimeout(() => setShowSoapRevealEffect(false), 1400);
      shouldAnimateNextSoapRef.current = false;
      previousSoapNoteRef.current = soapNote;
      return () => clearTimeout(timer);
    }
    previousSoapNoteRef.current = soapNote;
  }, [soapNote, isDemoMode]);

  useEffect(() => {
    setUploadTranscriptionStream({ active: false, lines: [], status: "" });
    shownCompletionToastKeysRef.current.clear();
  }, [activeSession]);

  //Automatically show the transcription panel whenever there is transcript data
  useEffect(() => {
    if (grammarCheck.correctedTranscript?.trim() && !showTranscriptionInSoapTab) {
      setShowTranscriptionInSoapTab(true);
    }
  }, [grammarCheck.correctedTranscript, showTranscriptionInSoapTab]);

  useEffect(() => {
    const subscribe = recording.subscribeLiveUi;
    const getSnapshot = recording.getLiveUiSnapshot;
    if (typeof subscribe !== "function" || typeof getSnapshot !== "function") {
      return undefined;
    }
    const maybeShow = () => {
      if (getSnapshot()?.transcript?.trim()) {
        setShowTranscriptionInSoapTab(true);
      }
    };
    maybeShow();
    return subscribe(maybeShow);
  }, [recording.subscribeLiveUi, recording.getLiveUiSnapshot]);

  const showCompletionToastOnce = useCallback((key, message, options = {}) => {
    if (!key) return;
    if (shownCompletionToastKeysRef.current.has(key)) return;
    shownCompletionToastKeysRef.current.add(key);
    // Avoid unbounded growth in very long sessions
    if (shownCompletionToastKeysRef.current.size > 80) {
      const first = shownCompletionToastKeysRef.current.values().next().value;
      shownCompletionToastKeysRef.current.delete(first);
    }
    toast.success(message, options);
  }, []);

  const applyTranscriptFieldsFromSessionData = useCallback((sessionData) => {
    if (recording.isRecording) return;
    let text = "";
    let tid = "";
    if (sessionData.transcript && String(sessionData.transcript).trim()) {
      text = String(sessionData.transcript).trim();
    } else if (sessionData.transcriptions && sessionData.transcriptions.length > 0) {
      const latest = sessionData.transcriptions[0];
      if (latest.conversation) text = String(latest.conversation).trim();
      else if (latest.transcriptionText) text = String(latest.transcriptionText).trim();
      if (latest.transcriptionId) tid = String(latest.transcriptionId).trim();
    }
    if (tid) {
      lastSoapAttemptRef.current.transcriptionId = tid;
      setTranscriptionId(tid);
    }
    if (text) {
      lastSoapAttemptRef.current.transcriptionText = text;
      recording.setFullTranscriptText(text);
      if (sessionData.correctedTranscript && String(sessionData.correctedTranscript).trim()) {
        grammarCheck.setCorrectedTranscript(sessionData.correctedTranscript);
      } else {
        grammarCheck.setCorrectedTranscript("");
      }
    } else {
      recording.setFullTranscriptText("");
      grammarCheck.clearGrammarCheck();
    }
  }, [recording, grammarCheck]);

  /**
   * Reload/session-switch: final transcript is in S3/DB but SOAP never landed.
   * Restore the regenerate CTA without restarting the visit.
   * Parked live visits (temp only, no transcriptions row) are not treated as failures.
   */
  const restoreRegenerateSoapFromSession = useCallback((sessionData) => {
    if (recording.isRecording) return false;
    if (soapNoteRef.current) return false;
    const hasSoap =
      Array.isArray(sessionData?.soapNotes) && sessionData.soapNotes.length > 0;
    if (hasSoap) return false;

    const latest =
      Array.isArray(sessionData?.transcriptions) && sessionData.transcriptions[0]
        ? sessionData.transcriptions[0]
        : null;
    if (!latest) return false;

    const text = String(
      latest.conversation || latest.transcriptionText || ""
    ).trim();
    const tid = latest.transcriptionId
      ? String(latest.transcriptionId).trim()
      : "";
    const hasFinalTranscript =
      Boolean(tid) ||
      Boolean(latest.transcriptionS3Key) ||
      Boolean(text);
    if (!hasFinalTranscript) return false;

    if (text) {
      lastSoapAttemptRef.current.transcriptionText = text;
      recording.setTranscript(text);
      if (typeof recording.setFullTranscriptText === "function") {
        recording.setFullTranscriptText(text);
      }
    }
    if (tid) {
      lastSoapAttemptRef.current.transcriptionId = tid;
      setTranscriptionId(tid);
    }
    soapGenerationFailedRef.current = true;
    setSoapGenerationFailed(true);
    setSoapGenerationFailedAgain(false);
    setShowSoapGenerationFailedModal(false);
    setActiveTab("soap-note");
    setShowTranscriptionInSoapTab(true);
    return true;
  }, [recording]);

  const handleShowTranscriptionInSoapTab = useCallback(() => {
    setShowTranscriptionInSoapTab(true);
    setIsTranscriptionCollapsed(false);
    setActiveTab("soap-note");
    setScrollToTranscriptionNonce((n) => n + 1);
  }, []);

  // Scroll to the transcript only when Transcription → Ready is clicked.
  // Ease toward the section and keep correcting until it sits under the header.
  // A single native smooth scroll was stopping short of Live Transcription.
  useEffect(() => {
    if (!scrollToTranscriptionNonce) return undefined;
    let cancelled = false;
    let frame = 0;
    let frames = 0;
    const step = () => {
      if (cancelled) return;
      frames += 1;
      const el = document.getElementById("inline-transcription-area");
      const container = soapScrollRef.current;
      if (!el || !container || el.getBoundingClientRect().height === 0) {
        if (frames < 40) frame = window.requestAnimationFrame(step);
        return;
      }
      const header = container.querySelector("[data-session-header]");
      const headerHeight = header?.getBoundingClientRect().height ?? 0;
      const desiredTop = container.getBoundingClientRect().top + headerHeight + 8;
      const delta = el.getBoundingClientRect().top - desiredTop;
      if (Math.abs(delta) <= 2 || frames > 180) return;
      const max = Math.max(0, container.scrollHeight - container.clientHeight);
      const next = Math.min(max, Math.max(0, container.scrollTop + delta * 0.22));
      if (Math.abs(next - container.scrollTop) < 0.5) return;
      container.scrollTop = next;
      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [scrollToTranscriptionNonce]);

  // Load the account-level HPI preference once; seed the per-chart selection.
  // Also load SOAP typing-animation preference (default off).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMyProfile();
        const profile = me?.data?.profile;
        const level = profile?.hpiDepthLevel;
        if (!cancelled && typeof level === "number" && level >= 1 && level <= 3) {
          setAccountHpiLevel(level);
          setChartHpiLevel(level);
        }
        if (!cancelled) {
          soapTypingAnimationEnabledRef.current = Boolean(
            profile?.soapTypingAnimationEnabled
          );
        }
      } catch (e) {
        // Non-fatal: fall back to the default level / animation off.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset per-chart HPI back to the account default when starting a new chart.
  useEffect(() => {
    if (!soapNote) {
      setChartHpiLevel(accountHpiLevel);
    }
  }, [activeSession, soapNote, accountHpiLevel]);

  const handleCopyTranscription = useCallback(() => {
    const textToCopy =
      grammarCheck.correctedTranscript?.trim() ||
      recording.getLiveUiSnapshot?.()?.transcript?.trim() ||
      recording.transcript?.trim() ||
      "";
    if (!textToCopy) {
      toast.error("No transcription text to copy");
      return;
    }
    navigator.clipboard.writeText(textToCopy).then(() => {
      toast.success("Transcription copied to clipboard");
    }).catch(() => {
      toast.error("Failed to copy transcription");
    });
  }, [grammarCheck.correctedTranscript, recording]);

  const transcriptionPanel = useMemo(() => {
    if (!showTranscriptionInSoapTab) return null;
    return (
      <div
        id="inline-transcription-area"
        className="mt-6 border rounded-xl bg-white shadow-sm"
      >
        <div
          className="flex justify-between items-center p-4 border-b cursor-pointer select-none"
          onClick={() => setIsTranscriptionCollapsed((prev) => !prev)}
        >
          <div className="flex items-center gap-2">
            {isTranscriptionCollapsed ? (
              <ChevronRight size={18} className="text-gray-600" />
            ) : (
              <ChevronDown size={18} className="text-gray-600" />
            )}
            <h3 className="text-xl font-semibold text-gray-800">
              {recording.isRecording
                ? recording.isPaused
                  ? "⏸ Paused"
                  : "🔴 Recording..."
                : "Live Transcription"}
            </h3>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopyTranscription();
            }}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
            title="Copy transcription"
          >
            <Copy size={18} className="text-gray-600" />
          </button>
        </div>
        <LiveTranscriptionPanelBody
          recording={recording}
          grammarCheck={grammarCheck}
          uploadTranscriptionStream={uploadTranscriptionStream}
          isTranscriptionCollapsed={isTranscriptionCollapsed}
        />
      </div>
    );
  }, [
    showTranscriptionInSoapTab,
    isTranscriptionCollapsed,
    handleCopyTranscription,
    recording,
    recording.isRecording,
    recording.isPaused,
    recording.isParked,
    recording.transcriptionSource,
    grammarCheck,
    uploadTranscriptionStream,
  ]);
  const {
    trialStatus,
    canAccessFeature,
    isAiFeatureLocked,
    isProPlusLocked,
    isChartCreationLocked,
    getAccountLimitMessage,
    showTrialExhaustedModal,
    showPlanReminderModal,
    closeTrialExhaustedModal,
    navigateToPayment,
    navigateToPricing,
    refreshTrialStatus,
  } = useTrial();

  useEffect(() => {
    if (!consumePostPaymentScribeRefresh()) return;
    cleanupStaleUiOverlays();
    closeTrialExhaustedModal();
    refreshTrialStatus();
    // Run once on scribe load after payment redirect (full page navigation).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Routes outside the scribe index render their own settings/page content.
  const isSettingsRoute = location.pathname !== "/scribe";

  const handleTrialExhaustedModalClose = useCallback(() => {
    closeTrialExhaustedModal();
    if (!activeSession && !isSettingsRoute) {
      setAwaitingVisitSelection(true);
    }
  }, [closeTrialExhaustedModal, activeSession, isSettingsRoute]);

  // Show create-visit coach after trial / plan reminder modals are dismissed
  useEffect(() => {
    if (
      showTrialExhaustedModal ||
      showPlanReminderModal ||
      isLoadingSessions ||
      isSettingsRoute ||
      activeSession ||
      isViewingDemoSnapshot
    ) {
      return;
    }
    setAwaitingVisitSelection(true);
  }, [
    showTrialExhaustedModal,
    showPlanReminderModal,
    isLoadingSessions,
    isSettingsRoute,
    activeSession,
    isViewingDemoSnapshot,
  ]);

  /** Pre-connect WebSocket so live capture does not fail on first use after page load. */
  useEffect(() => {
    if (isSettingsRoute) return;
    const streamingUserId =
      currentUser?.userId ?? currentUser?.userid ?? currentUser?.id ?? null;
    if (!streamingUserId) return;
    void ensureWebSocketReady(String(streamingUserId), authToken || "", 12000);
  }, [currentUser, authToken, isSettingsRoute]);

  // Navigation handlers for maintenance modal
  const handleNavigateToSettings = () => {
    setShowMaintenanceModal(false);
    navigate("/scribe/settings");
  };

  const handleNavigateToSupport = () => {
    setShowMaintenanceModal(false);
    navigate("/support-ticket");
  };

  const handleSoapNoteUpdate = useCallback((updatedSoapNote) => {
    setSoapNote(updatedSoapNote);
  }, []);
  const updateSearchParamsIfChanged = useCallback(
    (mutator, options = { replace: true }) => {
      const next = new URLSearchParams(searchParams);
      mutator(next);
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, options);
      }
    },
    [searchParams, setSearchParams],
  );

  // Consolidated URL + route state synchronizer
  useEffect(() => {
    if (isSettingsRoute) return;
    // Demo visits are not real sessions. A leftover sessionId/soapNoteId from a
    // previous real visit must not reload API data over demo snapshot state —
    // that race is what made clicking a completed demo flicker/blank after
    // 2–3 clicks (production latency makes the in-flight overwrite obvious).
    if (
      isDemoMode ||
      isViewingDemoSnapshot ||
      isViewingDemoSnapshotRef.current ||
      activeSession === "demo-session" ||
      intendedSessionIdRef.current === "demo-session"
    ) {
      return;
    }

    const soapNoteIdFromUrl = searchParams.get("soapNoteId");
    const sessionIdFromUrl = searchParams.get("sessionId");
    const assignedFromUrl = searchParams.get("assigned");
    const adminViewFromUrl = searchParams.get("adminView");
    const isInvalidSessionId = sessionIdFromUrl === "undefined" || sessionIdFromUrl === "null";
    const isInvalidSoapId = soapNoteIdFromUrl === "undefined" || soapNoteIdFromUrl === "null";

    if (isInvalidSessionId || isInvalidSoapId) {
      updateSearchParamsIfChanged((next) => {
        if (isInvalidSessionId) next.delete("sessionId");
        if (isInvalidSoapId) next.delete("soapNoteId");
      });
      return;
    }

    if (soapNoteIdFromUrl && soapNoteIdFromUrl !== "undefined") {
      if (sessionIdFromUrl) {
        updateSearchParamsIfChanged((next) => next.delete("sessionId"));
        return;
      }
      const shouldLoadAssigned =
        assignedFromUrl === "true" ||
        adminViewFromUrl === "true" ||
        (!sessionIdFromUrl && soapNoteIdFromUrl);
      if (shouldLoadAssigned && (soapNote?.soapNoteId !== soapNoteIdFromUrl || !isAssignedSOAPNote)) {
        loadAssignedSOAPNote(soapNoteIdFromUrl);
      }
      return;
    }

    if (location.pathname === "/scribe" && !isLoadingSessions && activeSession && !sessionIdFromUrl) {
      updateSearchParamsIfChanged((next) => next.set("sessionId", activeSession));
      return;
    }

    if (sessionIdFromUrl === "demo-session") {
      updateSearchParamsIfChanged((next) => next.delete("sessionId"));
      return;
    }

    if (sessionIdFromUrl && sessionIdFromUrl !== activeSession) {
      selectSession(sessionIdFromUrl);
    }
  }, [
    searchParams,
    location.pathname,
    isLoadingSessions,
    activeSession,
    soapNote,
    isAssignedSOAPNote,
    updateSearchParamsIfChanged,
    isSettingsRoute,
    isDemoMode,
    isViewingDemoSnapshot,
  ]);
  // Helper function
  const formatSessionDate = (date = new Date()) => {
    return date.toISOString().split("T")[0];
  };

  // Stable callback for precharting data changes
  const handlePrechartingDataChange = useCallback((data) => {
    return data;
  }, []);

  // Load sessions once on first mount; returning from settings/templates must not re-init (would reset visit).
  useEffect(() => {
    if (isSettingsRoute) {
      setIsLoadingSessions(false);
      return;
    }
    if (trialStatus.isLoading) {
      return;
    }
    if (hasLoadedInitialDataRef.current) {
      const storedId =
        activeSessionRef.current ||
        (typeof window !== "undefined"
          ? localStorage.getItem("activeSessionId")
          : null);
      const urlSid = searchParams.get("sessionId");
      const validStored =
        storedId &&
        storedId !== "undefined" &&
        storedId !== "null" &&
        storedId !== "demo-session";
      if (validStored && urlSid !== storedId) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("sessionId", storedId);
            return next;
          },
          { replace: true },
        );
      }
      return;
    }
    hasLoadedInitialDataRef.current = true;
    loadInitialData();
  }, [isSettingsRoute, trialStatus.isLoading]);

  // Add this useEffect to handle pending tab changes
  useEffect(() => {
    if (!isLoadingSessions && pendingTabChange) {
      setActiveTab(pendingTabChange);
      setPendingTabChange(null);
    }
  }, [isLoadingSessions, pendingTabChange]);

  // Show the welcome choice only for a genuinely new user. Returning users
  // should go straight back to their previous active visit.
  useEffect(() => {
    if (!initialDataLoaded) return;
    if (isLoadingSessions) return;
    if (showTrialExhaustedModal || showPlanReminderModal) return;

    if (sessions.length > 0) {
      setShowWelcomeModal(false);
      setAwaitingVisitSelection(false);
      return;
    }
    const urlSid = searchParams.get("sessionId");
    const hasUrlSession =
      urlSid && urlSid !== "undefined" && urlSid !== "null";
    if (activeSession || hasUrlSession) {
      setShowWelcomeModal(false);
      return;
    }

    const hasSeenWelcome = localStorage.getItem("hasSeenWelcomeModal");
    if (!hasSeenWelcome) {
      setShowWelcomeModal(true);
    }
  }, [
    initialDataLoaded,
    isLoadingSessions,
    showTrialExhaustedModal,
    showPlanReminderModal,
    sessions.length,
    activeSession,
    searchParams,
  ]);

  // Reset daily session creation flag when a new day starts
  useEffect(() => {
    const checkAndResetDailyFlag = () => {
      const hasCreatedSessionToday = localStorage.getItem(
        "hasCreatedSessionToday"
      );
      const today = new Date().toDateString();

      if (hasCreatedSessionToday && hasCreatedSessionToday !== today) {
        localStorage.removeItem("hasCreatedSessionToday");
      }
    };

    // Check on component mount
    checkAndResetDailyFlag();

    // Set up interval to check every hour
    const interval = setInterval(checkAndResetDailyFlag, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const isProvidersTab = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const activeTabParam = queryParams.get("tab");
    return location.pathname.endsWith("/settings") &&
      (activeTabParam === "providers-database" || activeTabParam === "providers-directory");
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isProvidersTab) return;

    const storedJobId = localStorage.getItem("scrapeJobId");
    if (!storedJobId) return;
    const initTimer = setTimeout(() => {
      let pollInterval;
      const startPolling = (jobId) => {
        if (pollInterval) clearInterval(pollInterval);
        pollInterval = setInterval(async () => {
          try {
            const progressRes = await getScrapeProgress(jobId);
            const responseData = progressRes.data;
            const job = responseData?.data;

            if (responseData?.__meta?.message === 'Progress retrieved' && job) {
              if (job.status === 'completed' || job.status === 'error') {
                clearInterval(pollInterval);
                localStorage.removeItem("scrapeJobId");
                localStorage.removeItem("scrapeFormNumber");
              }
            }
          } catch (err) {
            const status = err.response?.status;
            if (status === 404 || status === 403) {
              clearInterval(pollInterval);
              localStorage.removeItem("scrapeJobId");
              localStorage.removeItem("scrapeFormNumber");
              if (status === 403) console.warn('[scrapeProgress] Stale job belongs to another user — cleared.');
            }
          }
        }, 8000);
      };

      startPolling(storedJobId);

      return () => {
        if (pollInterval) clearInterval(pollInterval);
      };
    }, 1500);

    return () => clearTimeout(initTimer);
  }, [isProvidersTab]);

  const isPatientDetailsFilled = useCallback(() => {
    if (!patientContext) return false;
    const hasName =
      !!(patientContext.name?.trim?.() ||
        `${patientContext.firstName || ""} ${patientContext.lastName || ""}`.trim());
    const hasVisit =
      typeof patientContext.visitType === "string"
        ? patientContext.visitType.trim().length > 0
        : !!patientContext.visitType;
    return (
      !!hasName &&
      hasVisit
      // && !!patientContext.dateOfService
    );
  }, [patientContext]);

  // Update the handleTabChange function
  const handleTabChange = useCallback(async (tabValue) => {
    // Admin can access all tabs on every chart (including assigned SOAP notes)
    if (isAssignedSOAPNote && !isAdmin) {
      const restrictedTabs = [
        "patient",
        "upload",
        "transcription",
      ];

      // Allow switching to grammar check if needed, but otherwise restrict
      if (restrictedTabs.includes(tabValue) && tabValue !== "transcription") {
        toast.error(
          "This tab is not available for assigned SOAP notes. Please edit the SOAP note directly."
        );
        return;
      }

      // If trying to leave soap-note tab, show warning
      if (activeTab === "soap-note" && tabValue !== "soap-note") {
        const confirmLeave = window.confirm(
          "You're viewing an assigned SOAP note. Are you sure you want to leave? You can only edit the SOAP note in this view."
        );
        if (!confirmLeave) return;
      }
    }

    // Check account limits for upload / transcription tabs
    if (tabValue === "upload" || tabValue === "transcription") {
      if (isAiFeatureLocked) {
        const toastKey = `trial-exhausted-${tabValue}`;
        if (!toastShownRef.current[toastKey]) {
          toastShownRef.current[toastKey] = true;
          toast.error(getAccountLimitMessage());

          setTimeout(() => {
            toastShownRef.current[toastKey] = false;
          }, 3000);
        }
        return;
      }

      const hasAccess = await canAccessFeature();
      if (!hasAccess) {
        const toastKey = `trial-exhausted-${tabValue}-access`;
        if (!toastShownRef.current[toastKey]) {
          toastShownRef.current[toastKey] = true;
          toast.error(getAccountLimitMessage());

          setTimeout(() => {
            toastShownRef.current[toastKey] = false;
          }, 3000);
        }
        return;
      }
    }

    if (
      (tabValue === "transcription" || tabValue === "upload") &&
      !isPatientDetailsFilled()
    ) {
      const toastKey = `patient-details-required-${tabValue}`;
      if (!toastShownRef.current[toastKey]) {
        toastShownRef.current[toastKey] = true;
        toast.error("Please fill in patient details first before proceeding");
      }
      return;
    }

    if (isLoadingSessions) {
      setPendingTabChange(tabValue);
    } else {
      setActiveTab(tabValue);
    }
  }, [isAssignedSOAPNote, isAdmin, activeTab, canAccessFeature, isAiFeatureLocked, getAccountLimitMessage, isPatientDetailsFilled, isLoadingSessions]);

  const handleTabChangeRef = useRef(handleTabChange);
  useEffect(() => {
    handleTabChangeRef.current = handleTabChange;
  }, [handleTabChange]);

  const launchProductTour = useCallback(async ({ showSkip = false } = {}) => {
    const launchId = ++productTourLaunchRef.current;
    setTourShowSkip(showSkip);
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setMobileMenuOpen(true);
    }
    setKeepTourPanelOpen(true);
    productTourSeenRef.current = false;
    demoTour.resetTour();
    setTourTemplateOpen(false);
    setTourRecordingOpen(false);
    setTourStepIndex(0);
    setTourKey((prev) => prev + 1);
    demoTourStartedRef.current = true;

    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    await waitForTourTarget('[data-tour="new-visit"]', 2500);
    if (launchId !== productTourLaunchRef.current) return;

    demoTour.startTour();
  }, [demoTour]);

  launchProductTourRef.current = launchProductTour;

  // Welcome modal handlers
  const handleWelcomeModalClose = () => {
  setShowWelcomeModal(false);
  localStorage.setItem("hasSeenWelcomeModal", "true");
  setAwaitingVisitSelection(false);
};

  const handleStartVisit = async () => {
  setShowWelcomeModal(false);
  localStorage.setItem("hasSeenWelcomeModal", "true");
  await createNewSession();
};


  // ----- Helper to clear demo state -----
const clearDemoState = useCallback(() => {
  isViewingDemoSnapshotRef.current = false;
  setIsDemoMode(false);
  setIsViewingDemoSnapshot(false);
  if (intendedSessionIdRef.current === "demo-session") {
    intendedSessionIdRef.current = null;
  }
  if (activeSession === "demo-session") {
    activeSessionRef.current = null;
    setActiveSession(null);
    localStorage.removeItem("activeSessionId");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get("sessionId") === "demo-session") next.delete("sessionId");
      return next;
    }, { replace: true });
  }
  if (demoIntervalRef.current) {
    clearInterval(demoIntervalRef.current);
    demoIntervalRef.current = null;
  }
  if (recording.isRecording) {
    recording.stopTranscribing(() => {}, () => {});
  }
  // Do NOT clear the snapshot here — keep it for sidebar display until explicit deletion
}, [activeSession, recording, setActiveSession, setSearchParams]);

  // handleStartDemo – now only sets up demo environment and starts the tour.
  // Recording does NOT start automatically; the user will click Record later.
  const handleStartDemo = async () => {
    hasAutoStartedDemoRef.current = true;
    const hasPermission = await recording.checkMicPermission();
    if (!hasPermission) {
      setMicDeniedMessage(true);
      return;
    }

    setMicDeniedMessage(false);
    setShowWelcomeModal(false);
    localStorage.setItem("hasSeenWelcomeModal", "true");

    setSearchParams(new URLSearchParams(), { replace: true });
    demoTourStartedRef.current = false;
    setTourTemplateOpen(false);
    setTourRecordingOpen(false);

    flushSync(() => {
    intendedSessionIdRef.current = "demo-session";
    activeSessionRef.current = "demo-session";
    setActiveSession("demo-session");
    localStorage.setItem("activeSessionId", "demo-session");
    localStorage.setItem("lastSessionId", "demo-session");
    setIsAssignedSOAPNote(false);
    setIsDemoMode(true);
    setIsViewingDemoSnapshot(false);
    isViewingDemoSnapshotRef.current = false;
    setAwaitingVisitSelection(false);
    setPatientContext(DEMO_PATIENT_CONTEXT);
    setSoapNote(null);
    setActiveTab("patient");
    setShowTranscriptionInSoapTab(true);
    setIsTranscriptionCollapsed(false);
});
    void launchProductTour({ showSkip: false });

    toast.success("Demo mode activated! Follow the tour to start recording.");
  };

  /** Drives the simulated recording using the REAL recording hook (so timer,
   * pulsing mic, pause/resume UI are all authentic) but feeds a scripted
   * transcript instead of real Azure/Deepgram STT output.
   * Two flows share this function:
   *   - MANUAL (user presses Capture): full scripted transcript at a natural
   *     pace; the user must press Stop to generate the SOAP note.
   *   - AUTO (user presses Next on tour Step 5): a short, fast excerpt of the
   *     transcript, then capture stops itself and the SOAP note is generated,
   *     which advances the tour to Step 6.
   */
  const AUTO_DEMO_CAPTURE_LINES = 10;
  const AUTO_DEMO_TICK_MS = 700;
  const MANUAL_DEMO_TICK_MS = 1800;

  const runDemoRecording = ({ auto = false } = {}) => {
    autoStopDemoRef.current = auto;
    setLiveTranscribed(true);
    setMode("transcribing");

    // ★ Pass a `demo: true` flag to the recording hook so it knows to skip
    // actual media capture. The hook implementation must handle this flag.
    // (If the hook doesn't support it, this call will attempt to access the mic
    // and may fail — but we assume the hook has been updated accordingly.)
    recording.startRecording(
      () => { },
      () => { },
      resolveLiveStreamingLocale(
        currentUser?.profile?.defaultInputLanguages,
        currentUser?.profile?.language
      ),
      { audioSourceMode: "input", demo: true }, // ← demo flag added
    );

    // Plain-string accumulation only (no functional-update form) — some
    // custom hook setters don't support prev => ... the way useState does.
    let accumulatedText = "";
    let lineIndex = 0;
    const tickMs = auto ? AUTO_DEMO_TICK_MS : MANUAL_DEMO_TICK_MS;
    const maxLines = auto
      ? Math.min(AUTO_DEMO_CAPTURE_LINES, DEMO_TRANSCRIPT_LINES.length)
      : DEMO_TRANSCRIPT_LINES.length;

    if (demoIntervalRef.current) {
      clearInterval(demoIntervalRef.current);
      demoIntervalRef.current = null;
    }

    demoIntervalRef.current = setInterval(() => {
      // Frozen while the user has paused capture; resumes automatically
      // once isPausedRef flips back to false.
      if (isPausedRef.current) return;

      if (lineIndex >= maxLines) {
        clearInterval(demoIntervalRef.current);
        demoIntervalRef.current = null;

        if (autoStopDemoRef.current) {
          // AUTO flow (Next on Step 5): stop and generate the SOAP note.
          // generateDemoSoapNote() then advances the tour to Step 6.
          autoStopDemoRef.current = false;
          handleStopDemoEarly();
          return;
        }

        // MANUAL flow: transcript finished, prompt the user to click Stop.
        toast("Demo conversation complete. Click Stop to generate your SOAP note.", {
          duration: 4000,
        });
        return;
      }

      accumulatedText = accumulatedText
        ? `${accumulatedText}\n${DEMO_TRANSCRIPT_LINES[lineIndex]}`
        : DEMO_TRANSCRIPT_LINES[lineIndex];

      recording.setTranscript(accumulatedText);
      if (typeof recording.setFullTranscriptText === "function") {
        recording.setFullTranscriptText(accumulatedText);
      }
      lineIndex += 1;
    }, tickMs);
  };

  // Demo-safe "stop transcribing" — reuses the natural completion path
  const handleStopDemoEarly = () => {
    if (demoIntervalRef.current) {
      clearInterval(demoIntervalRef.current);
      demoIntervalRef.current = null;
    }
    autoStopDemoRef.current = false;
    recording.stopTranscribing(() => { }, () => { });
    setMode(null);
    generateDemoSoapNote();
  };

  /** AUTO flow — triggered by Next on tour Step 5: runs a short scripted
   * capture, auto-stops, generates the demo SOAP note, and lets
   * generateDemoSoapNote() advance the tour to Step 6. */
  const runAutoDemoCapture = () => {
    // Guards: a capture is already running, or the SOAP note is being
    // generated, or a note already exists (Back from Step 6 → Next).
    if (
      demoRecordingActiveRef.current ||
      demoIntervalRef.current ||
      demoBackgroundProgress.active ||
      soapNoteRef.current
    ) {
      return;
    }
    setActiveTab("soap-note");
    setShowTranscriptionInSoapTab(true);
    setIsTranscriptionCollapsed(false);
    setDemoBackgroundProgress({
    active: true,
    stage: "capturing",
    percent: 5,
    message: "Capturing conversation…",
  });

    runDemoRecording({ auto: true });
  };
  runAutoDemoCaptureRef.current = runAutoDemoCapture;

  /** Next on tour Step 5 (wired to DemoTour.onDemoCaptureNext). Closes the
   * tour dropdowns and kicks off the AUTO flow. The tooltip stays on Step 5
   * (disabled, "Capturing…") until generateDemoSoapNote() advances it. */
  const handleDemoCaptureNext = () => {
    setTourTemplateOpen(false);
    setTourRecordingOpen(false);
    runAutoDemoCaptureRef.current?.();
  };
  /** Generates (simulates) the demo SOAP note, then kicks off the guided
   * tour once the note + transcript panel have actually painted. */
const generateDemoSoapNote = () => {
  // Show the same background progress UI as real transcription
  setDemoBackgroundProgress({
    active: true,
    stage: "generating",
    percent: 10,
    message: "Drafting SOAP note sections...",
  });

  // Simulate progressive updates (optional)
  let progress = 10;
  const interval = setInterval(() => {
    progress += 15;
    if (progress >= 90) {
      clearInterval(interval);
      progress = 90;
    }
    setDemoBackgroundProgress((prev) => ({
      ...prev,
      percent: Math.min(progress, 90),
    }));
  }, 400);

  // After 3 seconds, set the SOAP note and clear progress
  setTimeout(() => {
    clearInterval(interval);
    setDemoBackgroundProgress({
      active: false,
      stage: "",
      percent: 0,
      message: "",
    });

    // transcript to the SOAP note metadata
    const demoTranscript = recording.getFullTranscriptionText?.() || DEMO_TRANSCRIPT_LINES.join('\n');
    const soapWithTranscript = {
      ...DEMO_SOAP_NOTE,
      metadata: {
        ...(DEMO_SOAP_NOTE.metadata || {}),
        transcript: demoTranscript,
      },
    };

    setSoapNote(soapWithTranscript);
    const ownerId = resolveAuthUserId(currentUser);
    const snapshot = {
      patientContext: DEMO_PATIENT_CONTEXT,
      soapNote: soapWithTranscript,
      sessionName: DEMO_PATIENT_CONTEXT.name,
      transcript: demoTranscript,
      createdAt: new Date().toISOString(),
      ownerUserId: ownerId,
    };
    // Persist only for the owning non-admin user. Admin All Visits must
    // never pick up this local demo from shared-browser storage.
    if (!isAdmin && ownerId) {
      setDemoVisitSnapshot(snapshot);
      demoVisitSnapshotRef.current = snapshot;
      saveDemoSnapshot(snapshot, ownerId);
    }

    intendedSessionIdRef.current = "demo-session";
    activeSessionRef.current = "demo-session";
    setActiveSession("demo-session");
    localStorage.setItem("activeSessionId", "demo-session");
    localStorage.setItem("lastSessionId", "demo-session");
    isViewingDemoSnapshotRef.current = true;
    setIsViewingDemoSnapshot(true);
    // DO NOT auto-start the tour here – it was already started in handleStartDemo.
    // The tour is already running; we only need to keep the panel expanded.
    setIsTranscriptionCollapsed(false);
    setShowTranscriptionInSoapTab(true); // ensure the panel is expanded
    toast.success("SOAP note generated successfully!", { duration: 2000 });


  // If the tour is parked on Capture, advance it to SOAP now that the demo
  // note exists. Covers both the AUTO flow (Next on Capture) and the MANUAL
  // flow (Capture → Stop). Refs are used because this runs inside a setTimeout.
    if (runTourRef.current && tourStepIndexRef.current === TOUR_STEP.CAPTURE) {
    setTourTemplateOpen(false);
    setTourRecordingOpen(false);
    setActiveTab("soap-note");
    // Wait for React to paint the SOAP note before Joyride targets it.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setTourStepIndex(TOUR_STEP.SOAP));
    });
  }
  }, 3000);
};

  const handleSelectDemoVisit = useCallback(() => {
  const ownerId = resolveAuthUserId(currentUser);
  const snapshot =
    demoVisitSnapshot ||
    demoVisitSnapshotRef.current ||
    loadDemoSnapshot(ownerId, { isAdmin });
  const selection = resolveDemoVisitSelection({
    snapshot,
    currentUserId: ownerId,
    isAdmin,
    isRecording: recording.isRecording,
  });
  if (!selection.ok) {
    if (selection.reason === "recording") {
      toast.error("Stop capture before viewing the demo visit.");
    }
    return;
  }

  // Claim this visit synchronously so any in-flight real-session fetch
  // (selectSession / loadSessionData) discards its result instead of
  // overwriting the demo SOAP after a slower production API response.
  intendedSessionIdRef.current = "demo-session";
  isViewingDemoSnapshotRef.current = true;
  activeSessionRef.current = "demo-session";
  demoVisitSnapshotRef.current = snapshot;
  if (!demoVisitSnapshot) setDemoVisitSnapshot(snapshot);

  setIsDemoMode(false);
  demoTourStartedRef.current = false;
  setActiveSession("demo-session");
  setActiveSessionName(snapshot.sessionName || "Demo Visit");
  setIsAssignedSOAPNote(false);
  setAwaitingVisitSelection(false);
  setIsViewingDemoSnapshot(true);
  setIsSwitchingSession(false);
  setIsLoadingSessions(false);
  localStorage.setItem("activeSessionId", "demo-session");
  localStorage.setItem("lastSessionId", "demo-session");

  // Strip real visit params only — do not replace the whole query on every
  // click or react-router + the URL sync effect ping-pong and glitch.
  setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    let changed = false;
    ["sessionId", "soapNoteId", "assigned", "adminView"].forEach((key) => {
      if (next.has(key)) {
        next.delete(key);
        changed = true;
      }
    });
    return changed ? next : prev;
  }, { replace: true });

  setPatientContext(snapshot.patientContext);
  setSoapNote(snapshot.soapNote);
  setActiveTab("soap-note");
  setShowTranscriptionInSoapTab(true);
  setIsTranscriptionCollapsed(false);
  if (snapshot.transcript) {
    recording.setFullTranscriptText(snapshot.transcript);
    recording.setTranscript(snapshot.transcript);
  }

}, [demoVisitSnapshot, recording, setSearchParams, currentUser, isAdmin]);

 const handleDeleteDemoVisit = useCallback(() => {
  demoVisitSnapshotRef.current = null;
  isViewingDemoSnapshotRef.current = false;
  setDemoVisitSnapshot(null);
  saveDemoSnapshot(null, resolveAuthUserId(currentUser));
  if (intendedSessionIdRef.current === "demo-session") {
    intendedSessionIdRef.current = null;
  }
  if (isViewingDemoSnapshot || activeSessionRef.current === "demo-session") {
    setIsViewingDemoSnapshot(false);
    activeSessionRef.current = null;
    setActiveSession(null);
    localStorage.removeItem("activeSessionId");
    clearSession();
  }
  toast.success("Demo visit removed");
}, [isViewingDemoSnapshot, currentUser]);

  const ownerUserId = resolveAuthUserId(currentUser);
  const demoSessionEntry = shouldShowDemoVisitInSessionList({
    isAdmin,
    isDemoMode,
    snapshot: demoVisitSnapshot,
  })
    ? isDemoMode
      ? {
          id: "demo-session",
          sessionName: "Demo Visit (in progress)",
          ownerUserId,
        }
      : {
          id: "demo-session",
          sessionName: demoVisitSnapshot.sessionName,
          createdAt: demoVisitSnapshot.createdAt,
          ownerUserId: demoVisitSnapshot.ownerUserId || ownerUserId,
        }
    : null;

  /** Demo-only template switch — simulates the same skeleton-loading UX as
   * the real regenerate-with-template flow, without hitting the backend
   * (the demo SOAP note has no real soapNoteId to regenerate against). */
const handleDemoTemplateSwitch = useCallback((templateInstanceId, meta = {}) => {
    const templateName = meta.templateName || "Selected Template";
    if (!demoTour.runTour) {
      setActiveTab("soap-note");
    }
    setTemplateRegenLabel(templateName);
    setTemplateRegenerating(true);
    setTimeout(() => {
      setSoapNote((prev) => ({
        ...prev,
        templateId: templateInstanceId,
        metadata: {
          ...(prev?.metadata || {}),
          templateInstanceId,
          templateName,
        },
      }));
      setTemplateRegenerating(false);
      setTemplateRegenLabel("");
      toast.success(`Updated with "${templateName}" (demo)`, { duration: 2200 });
    }, 1500);
}, [demoTour.runTour]);

  /** Called from the completion modal's "Start Your First Visit" CTA —
   * tears down demo state and creates a real, persisted session, landing on
   * its Patient Info tab exactly like a normal new-visit flow. */
  const handleExitDemoStartRealVisit = async () => {
    demoTour.resetTour();
    setShowDemoComplete(false);
    // Clear all demo state (but keep snapshot? We want to keep it in sidebar)
    clearDemoState();
    await createNewSession(); 
  };

const handleRestartTutorial = useCallback(() => {
  const currentPath = location.pathname;

  let sessionId = activeSession;
  if (!sessionId) {
    sessionId = typeof window !== "undefined" ? localStorage.getItem("activeSessionId") : null;
  }
  const targetPath = sessionId ? `/scribe?sessionId=${sessionId}` : "/scribe";

  if (currentPath !== "/scribe" && !currentPath.startsWith("/scribe?")) {
    navigate(targetPath);
    window.setTimeout(() => {
      void launchProductTour({ showSkip: true });
    }, 300);
    return;
  }
  void launchProductTour({ showSkip: true });
}, [launchProductTour, navigate, location.pathname, activeSession]);

const handleJoyrideCallback = useCallback((data, controls) => {
  const { status, type, step, index, action } = data || {};

  if (type === "tooltip") {
    productTourSeenRef.current = true;
  }

  if (type === "error:target_not_found") {
    try {
      controls?.next?.();
    } catch {
      /* last step or controls unavailable */
    }
    return;
  }

  // NOTE: Capture → Next in the demo never reaches here. DemoTour intercepts
  // that click (onDemoCaptureNext) so Joyride keeps the tooltip mounted while
  // the auto capture runs; generateDemoSoapNote() then sets stepIndex to SOAP.


  // This gives React time to render the dropdown before Joyride targets it.
  if (type === "step:after" && (action === "next" || action === "prev")) {
    const nextIndex = action === "next" ? index + 1 : index - 1;

    if (nextIndex === TOUR_STEP.TEMPLATES) {
      setTourTemplateOpen(true);
      setTourRecordingOpen(false);
    } else if (nextIndex === TOUR_STEP.RECORDING) {
      setTourTemplateOpen(false);
      setTourRecordingOpen(true);
    } else {
      setTourTemplateOpen(false);
      setTourRecordingOpen(false);
    }

    if (nextIndex === TOUR_STEP.SOAP) {
      setActiveTab("soap-note");
      if (isDemoMode && !soapNoteRef.current) setSoapNote(DEMO_SOAP_NOTE);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTourStepIndex(TOUR_STEP.SOAP));
      });
    } else {
      setTourStepIndex(Math.max(0, nextIndex));
    }
  }

  // Handle tab switching and fallback dropdown opening in step:before
  if (type === "step:before" || type === "tooltip_before") {
    setActiveTourStep(index);
    if (index === TOUR_STEP.PATIENT) {
      setActiveTab("patient");
      setDemoAnimationNonce((nonce) => nonce + 1);
      setTourTemplateOpen(false);
      setTourRecordingOpen(false);
    } else if (index === TOUR_STEP.TEMPLATES) {
      setActiveTab("patient");
      setTourTemplateOpen(true);
      setTourRecordingOpen(false);
    } else if (index === TOUR_STEP.RECORDING) {
      setTourTemplateOpen(false);
      setTourRecordingOpen(true);
    } else if (index === TOUR_STEP.CAPTURE) {
      setTourTemplateOpen(false);
      setTourRecordingOpen(false);
    } else if (index === TOUR_STEP.SOAP) {
      setTourTemplateOpen(false);
      setTourRecordingOpen(false);
      setActiveTab("soap-note");
      // Only seed the demo SOAP note if one isn't already there.
      if (isDemoMode && !soapNoteRef.current) {
        setSoapNote(DEMO_SOAP_NOTE);
      }
    }
  }

  // Mobile-specific sidebar handling
  if (typeof window !== "undefined" && window.innerWidth < 768) {
    const isStepBefore =
      type === "step:before" || type === "tooltip_before";
    const isStepAfter = type === "step:after";
    const isNewVisitTarget = step?.target === '[data-tour="new-visit"]';

    if (isStepBefore) {
      if (isNewVisitTarget) {
        setMobileMenuOpen(true);
      } else {
        setMobileMenuOpen(false);
      }
    } else if (isStepAfter && index === 0) {
      setMobileMenuOpen(false);
    }
  }

  const ended =
    status === STATUS.FINISHED ||
    status === STATUS.SKIPPED ||
    type === "tour:end";

  if (ended) {
    setTourStepIndex(0);
    setActiveTourStep(null);
    setTourTemplateOpen(false);
    setTourRecordingOpen(false);
    demoTour.setRunTour(false);
    setKeepTourPanelOpen(false);
    setMobileMenuOpen(false);
    if (productTourSeenRef.current) {
      markProductTourCompleted();
      demoTour.setTourFinished(true);
    }
  }
}, [demoTour, isDemoMode]);
// In MedicalScribeApp
const handleDemoIcdAdd = useCallback(async (code, description) => {
  if (!soapNote || soapNote.soapNoteId !== "demo-soap-note") return false;
  
  // Update assessment/plan sections using a helper (like DEMO_ICD_HELPER.addCode)
  // Example:
  const newCode = code.toUpperCase();
  const newHeading = `**${description} (${newCode}):**`;
  
  // Append to assessment
  let updatedAssessment = soapNote.structuredSections?.assessment || "";
  updatedAssessment += `\n- ${newHeading} New diagnosis added.`;
  
  // Update icd10Codes list
  const updatedCodes = [...(soapNote.icd10Codes || []), newCode];
  
  const updatedNote = {
    ...soapNote,
    icd10Codes: updatedCodes,
    structuredSections: {
      ...soapNote.structuredSections,
      assessment: updatedAssessment,
    },
  };
  updatedNote.assessment = updatedAssessment;
  
  setSoapNote(updatedNote);
  // Update snapshot if needed
  toast.success(`ICD code ${newCode} added (demo)`);
  return true;
}, [soapNote]);


  // ---- Handler for demo ICD changes (no API) ----
  const handleDemoIcdChange = useCallback(async (oldCode, newCode, newDescription) => {
    if (!soapNote || soapNote.soapNoteId !== "demo-soap-note") return false;

    const oldNorm = oldCode.toUpperCase();
    const newNorm = newCode.toUpperCase();

    // Update assessment and plan texts using the helper
    let updatedAssessment = DEMO_ICD_HELPER.replaceCode(
      soapNote.structuredSections?.assessment || "",
      oldNorm,
      newNorm,
      newDescription
    );
    let updatedPlan = DEMO_ICD_HELPER.replaceCode(
      soapNote.structuredSections?.plan || "",
      oldNorm,
      newNorm,
      newDescription
    );

    // Update ICD codes list
    const updatedCodes = (soapNote.icd10Codes || []).map(c =>
      c.toUpperCase() === oldNorm ? newNorm : c
    );
    if (!updatedCodes.includes(newNorm)) updatedCodes.push(newNorm);
    const finalCodes = updatedCodes.filter(c => c !== oldNorm);

    const updatedNote = {
      ...soapNote,
      icd10Codes: finalCodes,
      structuredSections: {
        ...soapNote.structuredSections,
        assessment: updatedAssessment,
        plan: updatedPlan,
      },
    };
    updatedNote.assessment = updatedAssessment;
    updatedNote.plan = updatedPlan;

    setSoapNote(updatedNote);
    toast.success(`ICD code updated: ${oldNorm} → ${newNorm}`);
    return true;
  }, [soapNote]);
  // ---- END NEW ----

  const getAuthUserId = () =>
    currentUser?.userId ?? currentUser?.userid ?? currentUser?.id ?? null;

  const refreshSessionsListSilently = async () => {
    try {
      const sessionsResponse = await sessionService.getUserSessions({
        status: "all",
        limit: 30,
        offset: 0,
        ...(isAdmin && { hasSoapNote: true }),
      });
      let sessionsData;
      if (sessionsResponse.success && sessionsResponse.data?.sessions) {
        sessionsData = sessionsResponse.data.sessions;
      } else if (sessionsResponse.success && sessionsResponse.body?.sessions) {
        sessionsData = sessionsResponse.body.sessions;
      } else if (
        sessionsResponse.success &&
        sessionsResponse.data?.data?.sessions
      ) {
        sessionsData = sessionsResponse.data.data.sessions;
      } else if (sessionsResponse.sessions) {
        sessionsData = sessionsResponse.sessions;
      }
      if (!sessionsData) return;
      const formattedSessions =
        typeof sessionService.formatSessionsList === "function"
          ? sessionService.formatSessionsList(sessionsData)
          : sessionsData;
      setSessions(sortSessionsNewestFirst(formattedSessions));
    } catch (error) {
      console.warn("Background session list refresh failed:", error);
    }
  };

  // Main initialization function
  const loadInitialData = async () => {
    try {
      setError(null);

      const soapNoteIdFromUrl = searchParams.get("soapNoteId");
      const urlSessionId = pathSessionId || searchParams.get("sessionId");
      const isValidSessionId = (sid) =>
        sid && sid !== "undefined" && sid !== "null";
      // Only restore a visit when sessionId is explicitly in the URL (reload / deep link).
      // Fresh logins land without a session so the create-visit coach can appear.
      const explicitUrlSessionId = isValidSessionId(urlSessionId)
        ? urlSessionId
        : isValidSessionId(initialUrlSessionIdRef.current)
          ? initialUrlSessionIdRef.current
          : null;

      // CASE A: Admin viewing assigned SOAP note
      if (soapNoteIdFromUrl && soapNoteIdFromUrl !== "undefined") {
        setIsLoadingSessions(true);
        return await loadAssignedSOAPNote(soapNoteIdFromUrl);
      }

      const userId = getAuthUserId();
      if (
        explicitUrlSessionId &&
        canFastRestoreFromCache(userId, explicitUrlSessionId)
      ) {
        const cached = readSessionsListCache(userId);
        const sessionToLoad = findCachedSession(cached, explicitUrlSessionId);
        if (sessionToLoad) {
          const formattedSessions = sortSessionsNewestFirst(cached.sessions || []);
          setSessions(formattedSessions);
          hasInitialSessionCreated.current = true;
          setAwaitingVisitSelection(false);
          void loadSessionData(sessionToLoad);
          void refreshSessionsListSilently();
          return true;
        }
      }

      // CASE B: Regular session view (cold start or no cache)
      setIsLoadingSessions(true);
      return await loadRegularSession(explicitUrlSessionId);
    } catch (error) {
      console.error("❌ Failed to load initial data:", error);
      setError("Failed to load data. Please refresh the page.");
      toast.error("Failed to load initial data");
      // Do not create a session on API error – avoids new session on every login when API fails or returns empty
    } finally {
      setIsLoadingSessions(false);
      setInitialDataLoaded(true);
    }
  };

  // Load assigned SOAP note
  const loadAssignedSOAPNote = async (soapNoteId) => {
    try {
      setIsAssignedSOAPNote(true);
      setActiveTab("soap-note");
      setInitialTab("soap-note");

      // Clean URL - only keep soapNoteId (remove any sessionId)
      const newParams = new URLSearchParams();
      newParams.set("soapNoteId", soapNoteId);
      newParams.set("tab", "soap-note");
      setSearchParams(newParams);

      // Fetch the SOAP note
      const response = await aiService.getSOAPNote(soapNoteId);
      if (!response || response.error) {
        throw new Error(response?.error || "Failed to fetch SOAP note");
      }

      // Extract SOAP note data
      let soapNoteData = null;
      let patientData = null;
      let sessionData = null;

      if (response.success === true || response.success === undefined) {
        // Try multiple response structures
        if (response.data?.body?.data?.soapNote) {
          soapNoteData = response.data.body.data.soapNote;
          patientData = response.data.body.data.patient;
          sessionData = response.data.body.data.session;
        } else if (response.body?.data?.soapNote) {
          soapNoteData = response.body.data.soapNote;
          patientData = response.body.data.patient;
          sessionData = response.body.data.session;
        } else if (response.data?.soapNote) {
          soapNoteData = response.data.soapNote;
          patientData = response.data.patient;
          sessionData = response.data.session;
        } else if (response.soapNote) {
          soapNoteData = response.soapNote;
          patientData = response.patient;
          sessionData = response.session;
        } else if (response.data?.data?.soapNote) {
          soapNoteData = response.data.data.soapNote;
          patientData = response.data.data.patient;
          sessionData = response.data.data.session;
        }
      }

      if (!soapNoteData) {
        throw new Error("No SOAP note data found");
      }

      setSoapNote(soapNoteData);

      // Set patient context with all required fields
      if (patientData) {
        const patientWithName = {
          ...patientData,
          name:
            patientData.firstName && patientData.lastName
              ? `${patientData.firstName} ${patientData.lastName}`.trim()
              : patientData.firstName ||
              patientData.lastName ||
              patientData.name ||
              "Unknown Patient",
          // Extract age from patient data
          age:
            patientData.age || patientData.dateOfBirth
              ? (() => {
                if (patientData.age) return patientData.age;
                if (patientData.dateOfBirth) {
                  const birthDate = new Date(patientData.dateOfBirth);
                  const today = new Date();
                  let age = today.getFullYear() - birthDate.getFullYear();
                  const m = today.getMonth() - birthDate.getMonth();
                  if (
                    m < 0 ||
                    (m === 0 && today.getDate() < birthDate.getDate())
                  ) {
                    age--;
                  }
                  return age;
                }
                return "--";
              })()
              : "--",
          // Extract gender
          gender: patientData.gender || "Not specified",
          // Extract encounter type from session data or SOAP note
          encounterType:
            sessionData?.encounterType ||
            soapNoteData.encounterType ||
            sessionData?.sessionName ||
            "--",
          // Extract service date
          dateOfService:
            sessionData?.visitDate || patientData.dateOfService || "--",
        };
        setPatientContext(patientWithName);
      } else {
        // If no patient data, set defaults
        setPatientContext({
          name: "Unknown Patient",
          age: "--",
          gender: "Not specified",
          encounterType: "--",
          dateOfService: "--",
        });
      }

      // For assigned SOAP notes, create a temporary session ID
      const tempSessionId = soapNoteData.sessionId || `temp-${soapNoteId}`;
      setActiveSession(tempSessionId);
      setActiveSessionName("Assigned SOAP Note");

      // Don't load regular sessions for admin view
      setSessions([]);

      return true;
    } catch (error) {
      console.error("❌ Error loading assigned SOAP note:", error);
      toast.error(
        "Failed to load assigned SOAP note. Loading regular session..."
      );
      setIsAssignedSOAPNote(false);

      // Fall back to regular session loading
      return await loadRegularSession(null);
    }
  };

  // Regular user: Load session
  const loadRegularSession = async (sessionIdFromUrl) => {
    const hydrateSessionFromApi = async (sessionId) => {
      if (!sessionId || sessionId === "undefined") return null;
      try {
        const response = await sessionService.getSession(sessionId);
        let sessionData;
        if (response.success && response.data?.session) {
          sessionData = response.data.session;
        } else if (response.success && response.body?.session) {
          sessionData = response.body.session;
        }
        if (!sessionData) return null;
        const formatted = sessionService.formatSessionForDisplay
          ? sessionService.formatSessionForDisplay(sessionData)
          : {
            id: sessionData.sessionId,
            sessionName: sessionData.sessionName,
            ...sessionData,
          };
        return {
          ...formatted,
          sessionName: formatted.sessionName ?? formatted.name,
        };
      } catch {
        return null;
      }
    };

    try {
      setIsAssignedSOAPNote(false);

      // Clean URL if we're in regular mode (remove soapNoteId if present)
      if (searchParams.has("soapNoteId")) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("soapNoteId");
        setSearchParams(newParams);
      }

      // Match SessionManager list filters so paginated sidebar rows align with this list
      const sessionsResponse = await sessionService.getUserSessions({
        status: "all",
        limit: 30,
        offset: 0,
        ...(isAdmin && { hasSoapNote: true }),
      });
      let formattedSessions = [];
      let sessionsData;

      if (sessionsResponse.success && sessionsResponse.data?.sessions) {
        sessionsData = sessionsResponse.data.sessions;
      } else if (sessionsResponse.success && sessionsResponse.body?.sessions) {
        sessionsData = sessionsResponse.body.sessions;
      } else if (
        sessionsResponse.success &&
        sessionsResponse.data?.data?.sessions
      ) {
        sessionsData = sessionsResponse.data.data.sessions;
      } else if (sessionsResponse.sessions) {
        sessionsData = sessionsResponse.sessions;
      }

      if (sessionsData) {
        if (typeof sessionService.formatSessionsList === "function") {
          formattedSessions = sessionService.formatSessionsList(sessionsData);
        } else {
          formattedSessions = sessionsData;
        }

        formattedSessions = sortSessionsNewestFirst(formattedSessions);
      }

      setSessions(formattedSessions);

      // Determine which session to load
      let sessionToLoad = null;
      const hasSessionIdInUrl =
        sessionIdFromUrl && sessionIdFromUrl !== "undefined";

      // No sessionId in URL = fresh login. Never auto-create a visit — users
      // with no visits see the create-visit coach; users with visits load via
      // Priority 2/3 below (all roles, every login).
      if (!hasSessionIdInUrl && !hasInitialSessionCreated.current) {
        hasInitialSessionCreated.current = true;
      }

      // Priority 1: Session from URL (reload / deep link)
      if (hasSessionIdInUrl) {
        sessionToLoad = formattedSessions.find((s) => s.id === sessionIdFromUrl);
        if (!sessionToLoad) {
          sessionToLoad = await hydrateSessionFromApi(sessionIdFromUrl);
          if (sessionToLoad) {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("sessionId", sessionIdFromUrl);
                return next;
              },
              { replace: true }
            );
            localStorage.setItem("activeSessionId", sessionIdFromUrl);
          }
        }
      }

      // Priority 2: Session from localStorage — only when URL had an explicit sessionId
      // but it could not be resolved from the list (reload / deep link fallback).
      if (!sessionToLoad && hasSessionIdInUrl) {
        const sessionIdFromStorage =
          localStorage.getItem("activeSessionId") ||
          localStorage.getItem("lastSessionId");
        if (sessionIdFromStorage) {
          sessionToLoad = formattedSessions.find((s) => s.id === sessionIdFromStorage);
          if (!sessionToLoad) {
            sessionToLoad = await hydrateSessionFromApi(sessionIdFromStorage);
          }
          if (sessionToLoad) {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("sessionId", sessionIdFromStorage);
                return next;
              },
              { replace: true }
            );
            localStorage.setItem("activeSessionId", sessionIdFromStorage);
          }
        }
      }

      // Returning users should resume their last real visit. If the stored
      // id is stale, fall back to the newest active visit instead of creating
      // a new one or showing a coach overlay.
      if (!sessionToLoad && !hasSessionIdInUrl && formattedSessions.length > 0) {
        const storedId =
          localStorage.getItem("activeSessionId") ||
          localStorage.getItem("lastSessionId");
        if (storedId && storedId !== "demo-session") {
          sessionToLoad = formattedSessions.find((s) => String(s.id) === String(storedId));
        }
        sessionToLoad = sessionToLoad || formattedSessions[0];
        if (sessionToLoad?.id) {
          localStorage.setItem("activeSessionId", String(sessionToLoad.id));
          localStorage.setItem("lastSessionId", String(sessionToLoad.id));
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set("sessionId", String(sessionToLoad.id));
              return next;
            },
            { replace: true }
          );
        }
      }

      if (sessionToLoad) {
        setAwaitingVisitSelection(false);
        await loadSessionData(sessionToLoad);
      } else if (hasSessionIdInUrl) {
        // Had sessionId in URL but couldn't load; sync from LS if present
        const storedId = localStorage.getItem("activeSessionId");
        if (storedId) {
          setActiveSession(storedId);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set("sessionId", storedId);
              return next;
            },
            { replace: true }
          );
        }
      } else {
        // A genuinely new user can choose demo or live visit in the welcome modal.
        setAwaitingVisitSelection(true);
      }

      return true;
    } catch (error) {
      console.error("❌ Failed to load regular session:", error);
      throw error;
    }
  };

  // Load session data
  const loadSessionData = async (session) => {
    setAwaitingVisitSelection(false);
    intendedSessionIdRef.current = session.id;
    setActiveSession(session.id);
    setActiveSessionName(cleanSessionName(session.sessionName));
    localStorage.setItem("activeSessionId", session.id);
    localStorage.setItem("lastSessionId", session.id);

    // Path-based session URLs are canonical for newly created visits.
    // Existing query-based deep links remain supported by the loader.
    if (!pathSessionId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sessionId", session.id);
        return next;
      }, { replace: true });
    }

    lastSoapAttemptRef.current = { transcriptionText: "", transcriptionId: "" };
    soapGenerationFailedRef.current = false;
    setSoapGenerationFailed(false);
    setSoapGenerationFailedAgain(false);
    setShowSoapGenerationFailedModal(false);

    try {
      // Fetch detailed session data including SOAP notes
      const response = await sessionService.getSession(session.id);
      let sessionData;
      if (response.success && response.data?.session) {
        sessionData = response.data.session;
      } else if (response.success && response.body?.session) {
        sessionData = response.body.session;
      } else {
        throw new Error("Invalid session response format");
      }

      if (intendedSessionIdRef.current !== session.id) {
        return;
      }

      // Set patient context
      if (sessionData.patient) {
        const patientWithName = {
          ...sessionData.patient,
          name: `${sessionData.patient.firstName || ""} ${sessionData.patient.lastName || ""}`.trim() || sessionData.patient.name,
          dateOfService: sessionData.patient.dateOfService || "",
        };
        setPatientContext(patientWithName);
      } else {
        setPatientContext({});
      }

      applyTranscriptFieldsFromSessionData(sessionData);

      // Check for SOAP notes
      if (sessionData.soapNotes && sessionData.soapNotes.length > 0) {
        const latestSoapNote = sessionData.soapNotes[0];
        if (!latestSoapNote.sessionId) {
          latestSoapNote.sessionId = session.id;
        }
        setSoapNote(latestSoapNote);
        setActiveTab("soap-note");
        releaseParkedAfterSoap(session.id);
      } else {
        setSoapNote(null);
        const restoredFailure = restoreRegenerateSoapFromSession(sessionData);
        if (!restoredFailure) {
          setActiveTab("patient");
        }
      }

      const sessionIdForAudio = session.id;
      void (async () => {
        try {
          if (intendedSessionIdRef.current !== sessionIdForAudio) return;
          const audioFile = await getAudioForSession(sessionIdForAudio);
          if (intendedSessionIdRef.current !== sessionIdForAudio) return;
          if (audioFile && !audioFile.isExpired) {
            const audioUrl = await getPlayableAudioUrl(sessionIdForAudio);
            if (audioUrl && intendedSessionIdRef.current === sessionIdForAudio) {
              audioPlayback.setAudioUrl(audioUrl);
            }
          } else {
            audioPlayback.clearAudio();
          }
        } catch (_) {
          /* non-blocking */
        }
      })();
    } catch (error) {
      // 404 = visit was deleted server-side (e.g. by an admin). Purge every
      // client-side reference to it instead of trying to render a dead visit.
      if (error?.response?.status === 404) {
        const deletedId = String(session.id);
        removeSessionFromListCache(getAuthUserId(), deletedId);
        if (localStorage.getItem("activeSessionId") === deletedId) {
          localStorage.removeItem("activeSessionId");
        }
        if (localStorage.getItem("lastSessionId") === deletedId) {
          localStorage.removeItem("lastSessionId");
        }
        if (intendedSessionIdRef.current === session.id) {
          const remaining = sessions.filter((s) => String(s.id) !== deletedId);
          setSessions(remaining);
          const nextId = remaining[0]?.id;
          if (nextId && nextId !== "undefined" && nextId !== "null") {
            await selectSession(nextId);
          } else {
            setActiveSession(null);
            setActiveSessionName("");
            setSearchParams({});
            clearSession();
          }
        }
        return;
      }
      console.error("Failed to load detailed session data:", error);
      if (intendedSessionIdRef.current === session.id) {
        recording.setTranscript("");
        grammarCheck.clearGrammarCheck();
      }
      // Fallback to using the session list object and loadSoapNotesForSession
      if (session.patient) {
        setPatientContext({ ...session.patient });
      }
      const hasSoapNotes = await loadSoapNotesForSession(session.id);
      if (hasSoapNotes) {
        setActiveTab("soap-note");
        releaseParkedAfterSoap(session.id);
      } else {
        const restoredFailure = restoreRegenerateSoapFromSession({
          soapNotes: [],
          transcriptions: [],
        });
        if (!restoredFailure) {
          setActiveTab("patient");
        }
      }
    }
  };

  // Load SOAP notes for a session
  const loadSoapNotesForSession = async (sessionId, preserveSoapOnMiss = false) => {
    try {
      const response = await aiService.getUserSOAPNotes(sessionId);
      // Backend uses `createResponse()` => { statusCode, body: { message, data } }.
      // With axios, this is returned under `response.data`.
      const payload = response?.data ?? response;
      const ok =
        payload?.statusCode === true ||
        payload?.statusCode === 200 ||
        payload?.statusCode === "200";

      const soapNotes =
        payload?.body?.data?.soapNotes ||
        payload?.body?.data?.data?.soapNotes ||
        payload?.body?.data?.soapNotes?.soapNotes ||
        payload?.data?.soapNotes ||
        [];

      if (ok && Array.isArray(soapNotes) && soapNotes.length > 0) {
        // API sorts by createdAt DESC, so index 0 is the latest.
        const latestSoapNote = soapNotes[0];
        if (!latestSoapNote.sessionId && sessionId) {
          latestSoapNote.sessionId = sessionId;
        }
        setSoapNote(latestSoapNote);
        return true;
      }

      if (!preserveSoapOnMiss) {
        setSoapNote(null);
      }
      return false;
    } catch (error) {
      if (!preserveSoapOnMiss) {
        setSoapNote(null);
      }
      return false;
    }
  };

  /** Fetch SOAP note entity for setState (caller may use flushSync). */
  const fetchSoapNoteByIdPayload = async (soapNoteId, fallbackSessionId = null) => {
    if (!soapNoteId) return null;
    try {
      const response = await aiService.getSOAPNote(soapNoteId);
      if (!response || response.error) return null;

      const root = response?.data ?? response;
      const ok =
        root?.statusCode === true ||
        root?.statusCode === 200 ||
        root?.statusCode === "200" ||
        response.success === true;

      let soapNoteData = null;
      const dataBlock = root?.body?.data ?? root?.data;

      if (dataBlock?.soapNote) {
        soapNoteData = dataBlock.soapNote;
      } else if (root?.body?.data?.soapNote) {
        soapNoteData = root.body.data.soapNote;
      } else if (response.data?.body?.data?.soapNote) {
        soapNoteData = response.data.body.data.soapNote;
      } else if (response.body?.data?.soapNote) {
        soapNoteData = response.body.data.soapNote;
      } else if (response.data?.soapNote) {
        soapNoteData = response.data.soapNote;
      } else if (response.soapNote) {
        soapNoteData = response.soapNote;
      } else if (response.data?.data?.soapNote) {
        soapNoteData = response.data.data.soapNote;
      }

      if (!soapNoteData && ok && dataBlock?.soapNoteId) {
        soapNoteData = dataBlock;
      }

      if (!soapNoteData) return null;
      if (!soapNoteData.sessionId && fallbackSessionId) {
        soapNoteData = { ...soapNoteData, sessionId: fallbackSessionId };
      }
      return soapNoteData;
    } catch {
      return null;
    }
  };

  // Create new session function
  const createNewSession = useCallback(async (silent = false) => {
    const stoppingThisVisit = Boolean(liveStopSessionIdRef.current);
    if (
      recording.isRecording &&
      !recording.isPaused &&
      !stoppingThisVisit &&
      !isCaptureFinished(activeSession)
    ) {
      if (!silent) {
        toast.error("Pause or stop capture before creating a new visit.");
      }
      return;
    }
    if (sessionCreationLock.current || isCreatingSession) {
      return;
    }

    try {
      sessionCreationLock.current = true;
      setIsCreatingSession(true);

      if (
        activeSession &&
        (recording.isRecording || recording.isParked) &&
        !stoppingThisVisit &&
        !isCaptureFinished(activeSession)
      ) {
        void parkCurrentCaptureIfNeeded(activeSession, { autoPause: true });
      } else if (activeSession && (stoppingThisVisit || isCaptureFinished(activeSession))) {
        recording.endParkedCapture();
        markCaptureFinished(activeSession);
        clearParkedCapture(activeSession);
      }

      const today = new Date();
      const todayString = today.toDateString();
      const todaysSessions = sessions.filter((session) => {
        try {
          const sessionName = session.name || session.sessionName;
          const isTodaySession =
            sessionName &&
            sessionName.includes(`Visit ${formatSessionDate(today)}`);

          let isCreatedToday = false;
          if (session.createdAt) {
            const sessionDate = new Date(session.createdAt);
            isCreatedToday = sessionDate.toDateString() === todayString;
          }

          return isTodaySession || isCreatedToday;
        } catch {
          return false;
        }
      });

      const sessionCount = todaysSessions.length + 1;
      const sessionName = `Visit ${formatSessionDate(today)} #${sessionCount}`;

      const response = await sessionService.createSession({
        sessionName: sessionName,
        encounterType: "consultation",
        status: "active",
      });

      if (response.success && response.data?.session) {
        const newSession = sessionService.formatSessionForDisplay(
          response.data.session
        );
        const displaySession = {
          ...newSession,
          sessionName: newSession.sessionName || newSession.name,
          createdAt:
            response.data.session.createdAt ||
            newSession.createdAt ||
            new Date().toISOString(),
          visitDate: response.data.session.visitDate || newSession.visitDate,
        };
        if (isAdmin) {
          const profile = currentUser?.profile || {};
          const fullName =
            profile.firstName && profile.lastName
              ? `${profile.firstName} ${profile.lastName}`.trim()
              : currentUser?.email || "Unknown";
          displaySession.creator = {
            name: fullName,
            email: currentUser?.email,
            userId: currentUser?.userId || currentUser?.id,
          };
        }
        setSessions((prev) =>
          sortSessionsNewestFirst([
            displaySession,
            ...prev.filter((s) => String(s.id) !== String(displaySession.id)),
          ]),
        );
        setSessionsVersion((v) => v + 1);
        emitSessionPatch({ op: "add", session: displaySession });
        // User already opened the completed demo while createSession was in
        // flight — keep the new visit in the list but do not steal the view.
        if (
          intendedSessionIdRef.current === "demo-session" ||
          isViewingDemoSnapshotRef.current
        ) {
          return displaySession.id;
        }
        setAwaitingVisitSelection(false);
        setActiveSession(displaySession.id);
        localStorage.setItem("activeSessionId", displaySession.id);
        localStorage.setItem("lastSessionId", displaySession.id);
        setActiveSessionName(cleanSessionName(displaySession.sessionName));
        setSearchParams({ sessionId: displaySession.id }, { replace: true });
        setIsDemoMode(false);

        clearSession({ skipRecordingClear: true, keepVisitSelected: true });
        setAwaitingVisitSelection(false);
        setShowTranscriptionInSoapTab(false);
        setActiveTab("patient");
        toast.dismiss("live-stop-finalize");
        if (stoppingThisVisit && typeof recording.detachLiveTranscriptUi === "function") {
          recording.detachLiveTranscriptUi();
        } else {
          recording.clearRecording();
        }
        intendedSessionIdRef.current = displaySession.id;
        setError(null);

        if (!silent && !stoppingThisVisit) {
          toast.success("New session created successfully!");
        }
        return displaySession.id;
      } else {
        throw new Error("Failed to create session");
      }
    } catch (error) {
      console.error("Failed to create new session:", error);
      const errorCode =
        error?.data?.errorCode ||
        error?.body?.data?.errorCode ||
        error?.errorCode;
      const apiMessage =
        error?.message ||
        error?.body?.message ||
        "Failed to create new session. Please try again.";

      setError(apiMessage);
      if (!silent) {
        if (
          errorCode === "CHART_LIMIT_REACHED" ||
          errorCode === "TRIAL_PERIOD_EXPIRED" ||
          errorCode === "MONTHLY_NOTE_LIMIT_REACHED"
        ) {
          toast.error(apiMessage);
        } else {
          toast.error("Failed to create new session. Please try again.");
        }
      }
    } finally {
      sessionCreationLock.current = false;
      setIsCreatingSession(false);
    }
  }, [sessions, isCreatingSession, recording, activeSession, parkCurrentCaptureIfNeeded]);

  // the selectSession function - 
  const selectSession = useCallback(async (sessionId) => {
    const sid = sessionId ? String(sessionId) : null;

    if (sid === "demo-session") return;

    // If a demo is running and the user picked a REAL session, exit demo mode
    // and continue into the normal selection flow below.
    if (isDemoMode || isViewingDemoSnapshot || isViewingDemoSnapshotRef.current) {
      clearDemoState();
    }

    if (!sid || sid === "undefined" || sid === "null") {
      console.error("selectSession called with invalid sessionId:", sessionId);
      toast.error("Cannot load session: invalid session identifier");
      return;
    }

    setIsDemoMode(false);

    // Auto-park live capture on switch (no separate Pause required).
    // Only block while an explicit resume is in flight.
    if (isResumingCapture) {
      toast("Please wait — capture is resuming.", { icon: "⏳", duration: 2000 });
      return;
    }

    const clickTs = Date.now();
    if (activeSession === sid) return;
    if (inFlightSelectSessionRef.current === sid) return;
    if (lastSelectSessionRef.current.id === sid && clickTs - lastSelectSessionRef.current.ts < 400)
      return;

    lastSelectSessionRef.current = { id: sid, ts: clickTs };
    inFlightSelectSessionRef.current = sid;
    intendedSessionIdRef.current = sid;
    setLastSessionLoadTime(clickTs);

    // Clean URL when switching to regular session - remove soapNoteId
    const newParams = new URLSearchParams();
    newParams.set("sessionId", sid);
    setSearchParams(newParams);

    if (audioPlayback.isPlaying) {
      audioPlayback.stopAudio();
    }

    try {
      const alreadyHadSession = Boolean(activeSession);
      if (!alreadyHadSession) {
        setIsLoadingSessions(true);
      } else {
        setIsSwitchingSession(true);
      }
      setAwaitingVisitSelection(false);
      toast.dismiss("live-stop-finalize");

      const outgoingSid = activeSession ? String(activeSession) : null;
      const outgoingHasSoap =
        Boolean(soapNoteRef.current?.soapNoteId) ||
        (outgoingSid && isCaptureFinished(outgoingSid)) ||
        Boolean(
          sessions.find((s) => String(s.id) === outgoingSid)?.hasSoapNote
        );
      const stoppingOutgoing = Boolean(liveStopSessionIdRef.current);
      const skipPark =
        !outgoingSid ||
        outgoingHasSoap ||
        stoppingOutgoing ||
        isCaptureFinished(outgoingSid) ||
        !(recording.isRecording || recording.isParked);

      const selectedSession = sessions.find(
        (session) => String(session.id) === sid
      );
      const destHasSoap = Boolean(selectedSession?.hasSoapNote);
      const destShowSoap = destHasSoap || isCaptureFinished(sid);

      // Switch the highlighted visit first so the sidebar feels instant.
      setActiveSession(sid);
      intendedSessionIdRef.current = sid;
      setIsAssignedSOAPNote(false);
      setActiveTab(destShowSoap ? "soap-note" : "patient");
      setIsDemoMode(false);

      if (selectedSession) {
        setActiveSessionName(cleanSessionName(selectedSession.sessionName));
        if (selectedSession.patient) {
          const patientWithName = {
            ...selectedSession.patient,
            name:
              `${selectedSession.patient.firstName || ""} ${selectedSession.patient.lastName || ""
                }`.trim() || selectedSession.patient.name,
            dateOfService: selectedSession.patient.dateOfService || "",
          };
          setPatientContext(patientWithName);
        } else {
          setPatientContext({});
        }
      } else {
        setPatientContext({});
      }

      // Defer heavy clears so the session highlight paints before content resets.
      // Abort if the user already switched to the demo snapshot (or another
      // visit) — otherwise this wipe lands later and blanks the demo.
      startTransition(() => {
        if (intendedSessionIdRef.current !== sid) return;
        setShowTranscriptionInSoapTab(false);
        grammarCheck.clearGrammarCheck();
        setTranscriptionId("");
        setSoapNote(null);
        setError(null);
        lastSoapAttemptRef.current = { transcriptionText: "", transcriptionId: "" };
        resetSoapGenerationFailure();
        setUploadTranscriptionStream({ active: false, lines: [], status: "" });
        if (!skipPark && typeof recording.releaseCaptureUi === "function") {
          recording.releaseCaptureUi();
        }
        if (skipPark && !stoppingOutgoing) {
          recording.clearRecording();
        } else if (typeof recording.detachLiveTranscriptUi === "function") {
          recording.detachLiveTranscriptUi();
        }
      });

      setIsLoadingSessions(false);
      if (inFlightSelectSessionRef.current === sid) {
        inFlightSelectSessionRef.current = null;
      }

      if (!skipPark) {
        const parkSid = outgoingSid;
        void (async () => {
          const parked = await parkCurrentCaptureIfNeeded(parkSid, {
            autoPause: true,
          });
          if (parked) {
            toast("Capture saved for this visit — return anytime to resume.", {
              icon: "⏸",
              duration: 2800,
              id: `parked-${parkSid}`,
            });
          }
        })();
      } else if (outgoingSid && (outgoingHasSoap || stoppingOutgoing)) {
        markCaptureFinished(outgoingSid);
        clearParkedCapture(outgoingSid);
        recording.endParkedCapture();
      }

      void (async () => {
        try {
          const response = await sessionService.getSession(sid);
          let sessionData;
          if (response.success && response.data?.session) {
            sessionData = response.data.session;
          } else if (response.success && response.body?.session) {
            sessionData = response.body.session;
          } else {
            throw new Error("Invalid session response format");
          }

          if (intendedSessionIdRef.current !== sid) {
            return;
          }

          if (sessionData.patient) {
            const patientWithName = {
              ...sessionData.patient,
              name:
                `${sessionData.patient.firstName || ""} ${sessionData.patient.lastName || ""
                  }`.trim() || sessionData.patient.name,
              dateOfService: sessionData.patient.dateOfService || "",
            };
            setPatientContext(patientWithName);
          } else if (!selectedSession?.patient) {
            setPatientContext({});
          }

          let latestSoapNote =
            sessionData.soapNotes && sessionData.soapNotes.length > 0
              ? sessionData.soapNotes[0]
              : null;
          const knownSoap =
            Boolean(latestSoapNote) ||
            isCaptureFinished(sid) ||
            Boolean(selectedSession?.hasSoapNote);
          const restoredParked = await restoreParkedCaptureForSession(sid, {
            hasSoapNote:
              Boolean(latestSoapNote) || knownSoap || isCaptureFinished(sid),
          });
          if (intendedSessionIdRef.current !== sid) return;
          if (!restoredParked) {
            applyTranscriptFieldsFromSessionData(sessionData);
            setShowTranscriptionInSoapTab(false);
          }

          if (sessionData.audioUrl) {
            audioPlayback.setAudioUrl(sessionData.audioUrl);
          }

          if (latestSoapNote) {
            if (!latestSoapNote.sessionId) {
              latestSoapNote.sessionId = sid;
            }
            setSoapNote(latestSoapNote);
            setActiveTab("soap-note");
          } else if (restoredParked) {
            setActiveTab("soap-note");
          } else if (destHasSoap || isCaptureFinished(sid)) {
            const hasSoapNotes = await loadSoapNotesForSession(sid);
            if (intendedSessionIdRef.current !== sid) return;
            if (hasSoapNotes) {
              setActiveTab("soap-note");
            } else {
              const restoredFailure = restoreRegenerateSoapFromSession(sessionData);
              if (!restoredFailure) {
                setActiveTab("patient");
              }
            }
          } else {
            const restoredFailure = restoreRegenerateSoapFromSession(sessionData);
            if (!restoredFailure) {
              setActiveTab("patient");
            }
          }

          const audioSessionId = sid;
          try {
            if (intendedSessionIdRef.current !== audioSessionId) return;
            let audioFile = getAudioForSession(audioSessionId);
            if (!audioFile && !isCacheValid()) {
              const refreshed = await triggerAudioFetch();
              if (intendedSessionIdRef.current !== audioSessionId) return;
              audioFile =
                refreshed?.get?.(audioSessionId) ||
                getAudioForSession(audioSessionId);
            }
            if (intendedSessionIdRef.current !== audioSessionId) return;
            if (audioFile && !audioFile.isExpired) {
              const audioUrl = await getPlayableAudioUrl(audioSessionId);
              if (audioUrl && intendedSessionIdRef.current === audioSessionId) {
                audioPlayback.setAudioUrl(audioUrl);
              }
            } else if (!sessionData.audioUrl) {
              audioPlayback.clearAudio();
            }
          } catch (_) {
            /* non-blocking */
          }
        } catch (error) {
          if (intendedSessionIdRef.current !== sid) return;
          console.error("Failed to load session:", error);
          setError("Failed to load session data.");
          toast.error("Failed to load session. Please try again.");
        } finally {
          if (intendedSessionIdRef.current === sid) {
            setIsSwitchingSession(false);
          }
        }
      })();
    } catch (error) {
      console.error("Failed to switch session:", error);
      setIsSwitchingSession(false);
    } finally {
      setIsParkingCapture(false);
      if (inFlightSelectSessionRef.current === sid) {
        inFlightSelectSessionRef.current = null;
      }
    }
  }, [activeSession, audioPlayback, lastSessionLoadTime, sessions, recording, grammarCheck, getAudioForSession, getPlayableAudioUrl, triggerAudioFetch, isCacheValid, applyTranscriptFieldsFromSessionData, restoreRegenerateSoapFromSession, isDemoMode,isViewingDemoSnapshot, clearDemoState, parkCurrentCaptureIfNeeded, restoreParkedCaptureForSession, releaseParkedAfterSoap, isResumingCapture]);

  const updateSessionName = useCallback(async (sessionId, newName) => {
    try {
      const response = await sessionService.updateSession(sessionId, {
        sessionName: newName,
      });

      if (response.success) {
        setSessions((prev) =>
          prev.map((session) =>
            session.id === sessionId
              ? { ...session, name: newName, sessionName: newName }
              : session
          )
        );

        if (activeSession === sessionId) {
          setActiveSessionName(cleanSessionName(newName));
        }
        setSessionsVersion((v) => v + 1);
        emitSessionPatch({
          op: "upsert",
          sessionId,
          patch: { name: newName, sessionName: newName },
        });
        // toast.success("Session name updated successfully!");
      } else {
        throw new Error("Failed to update session name");
      }
    } catch (error) {
      console.error("Failed to update session name:", error);
      // toast.error("Failed to update session name. Please try again.");
    }
  }, [activeSession]);

const deleteSession = useCallback(async (sessionId, { skipApi = false, deletedIds } = {}) => {
    const removedIds = (deletedIds?.length ? deletedIds : [sessionId]).map(String);
    try {
      if (!skipApi) {
        if (deletedIds?.length > 1) {
          await sessionService.bulkDeleteSessions(deletedIds);
        } else {
          await sessionService.deleteSession(sessionId);
        }
      }

      removedIds.forEach((id) => {
        removeSessionFromListCache(getAuthUserId(), id);
        invalidateParkedPersist(id);
        clearParkedCapture(id);
        delete transcriptSegmentIndexRef.current[id];
        delete audioChunkIndexRef.current[id];
        delete uploadedAudioSegmentCountRef.current[id];
      });

      if (removedIds.length > 1) {
        emitSessionPatch({ op: "removeMany", sessionIds: removedIds });
      } else {
        emitSessionPatch({ op: "remove", sessionId: removedIds[0] });
      }

      const remainingSessions = sessions.filter(
        (s) => !removedIds.includes(String(s.id)),
      );
      setSessions(remainingSessions);

      if (activeSession && removedIds.includes(String(activeSession))) {
        if (remainingSessions.length > 0) {
          const newSessionId = remainingSessions[0].id;
          if (newSessionId && newSessionId !== "undefined" && newSessionId !== "null") {
            await selectSession(newSessionId);
          } else {
            setActiveSession(null);
            setActiveSessionName("");
            localStorage.removeItem("activeSessionId");
            setSearchParams({});
            clearSession();
          }
        } else {
          setActiveSession(null);
          setActiveSessionName("");
          localStorage.removeItem("activeSessionId");
          localStorage.removeItem("lastSessionId");
          setSearchParams({});
          clearSession();
        }
      }

      setError(null);
      setSessionsVersion((v) => v + 1);
    } catch (error) {
      const alreadyDeleted =
        error?.response?.status === 404 ||
        error?.response?.data?.body?.message?.includes?.("not found");
      if (alreadyDeleted && skipApi) {
        return;
      }
      console.error("Failed to delete session:", error);
      setError("Failed to delete session. Please try again.");
      toast.error("Failed to delete session. Please try again.");
    }
  }, [sessions, activeSession, isAdmin, selectSession, clearSession, emitSessionPatch, invalidateParkedPersist]);

  
  // handleStartTranscribing – if in demo mode, start the simulated recording.
  const handleStartTranscribing = async () => {
    // ---- DEMO MODE ----
    if (isDemoMode) {
      // Do not check for soapNote or patient details; the demo already has them.
      // Start the simulated transcript and switch to SOAP tab.
      runDemoRecording();
      setActiveTab("soap-note");
      setShowTranscriptionInSoapTab(true);
      return;
    }

    // ---- REAL MODE (unchanged) ----
    if (process.env.REACT_APP_MAINTENANCE_MODE === "true") {
      setShowMaintenanceModal(true);
      return;
    }

    if (isAiFeatureLocked) {
      toast.error(getAccountLimitMessage());
      return;
    }

    if (soapNote) {
      toast.error("A SOAP note already exists for this visit.");
      return;
    }

    if (!isPatientDetailsFilled()) {
      toast.error(
        "Please fill in patient details first before starting transcription"
      );
      return;
    }

    if (isLoadingSessions) {
      toast.error("Session is still loading. Please wait a moment.");
      return;
    }

    const sessionIdForCapture = resolveSessionIdForApi();
    if (!sessionIdForCapture || sessionIdForCapture === "undefined") {
      toast.error(
        "Visit session is not ready yet. Wait a moment or refresh the page."
      );
      return;
    }

    if (mode === "recording") {
      alert("Please stop recording before start transcribing.");
      return;
    }

    // Warm WebSocket while the user picks mic/screen-share — do not wait for it.
    const streamingFlagEarly = process.env.REACT_APP_ENABLE_STREAMING_TRANSCRIPTION;
    const serverLiveSttEnabledEarly =
      streamingFlagEarly == null || String(streamingFlagEarly).trim() === ""
        ? true
        : String(streamingFlagEarly).toLowerCase() === "true";
    const streamingUserIdEarly =
      currentUser?.userId ?? currentUser?.userid ?? currentUser?.id ?? null;
    if (serverLiveSttEnabledEarly && streamingUserIdEarly) {
      void ensureWebSocketReady(
        String(streamingUserIdEarly),
        authToken || "",
        12000,
      );
    }

    // getDisplayMedia must run before other awaits so the browser keeps user activation (Mac/Windows).
    let preacquiredDisplayStream = null;
    let presolvedDisplayCapture = null;
    if (needsDisplayMediaCapture(audioSourceMode)) {
      try {
        preacquiredDisplayStream = await acquireDisplayMediaStream();
      } catch (err) {
        toast.error(err?.message || "Screen share cancelled.");
        return;
      }

      presolvedDisplayCapture = await recording.ensureDisplayAudio(
        preacquiredDisplayStream,
        audioSourceMode
      );
      if (presolvedDisplayCapture?.cancelled) return;
    }

    const hasAccess = await canAccessFeature();
    if (!hasAccess) {
      stopMediaStream(preacquiredDisplayStream);
      toast.error(getAccountLimitMessage());
      return;
    }

    const needsMic = needsMicCapture(audioSourceMode);
    if (needsMic) {
      const hasPermission = await recording.checkMicPermission(
        audioInputDeviceId || undefined
      );
      if (!hasPermission) {
        stopMediaStream(preacquiredDisplayStream);
        return;
      }
    }

    if (isLoadingSessions) {
      setPendingTabChange("soap-note");
    } else {
      setActiveTab("soap-note");
    }
    setShowTranscriptionInSoapTab(true);

    // Server live STT (Deepgram / Azure) only for this action — not for Upload tab "record only".
    // /auth/me returns userId (not id); login() sets userid — must resolve both or streaming never starts.
    const streamingFlag = process.env.REACT_APP_ENABLE_STREAMING_TRANSCRIPTION;
    const serverLiveSttEnabled =
      streamingFlag == null || String(streamingFlag).trim() === ""
        ? true
        : String(streamingFlag).toLowerCase() === "true";
    const streamingUserId =
      streamingUserIdEarly ||
      currentUser?.userId ||
      currentUser?.userid ||
      currentUser?.id ||
      null;
    if (serverLiveSttEnabled && !streamingUserId) {
      toast.error(
        "Live server transcription needs a signed-in user. Refresh the page or sign in again."
      );
    }

    // No grammar API during live transcribe — avoids 1.5s+ debounce and network latency on every final chunk.
    grammarCheck.clearGrammarCheck();

    if (sessionIdForCapture) {
      clearCaptureFinished(sessionIdForCapture);
      invalidateParkedPersist(sessionIdForCapture);
      clearParkedCapture(sessionIdForCapture);
      transcriptSegmentIndexRef.current[String(sessionIdForCapture)] = 0;
      audioChunkIndexRef.current[String(sessionIdForCapture)] = 0;
      uploadedAudioSegmentCountRef.current[String(sessionIdForCapture)] = 0;
    }

    recording.startRecording(
      () => { },
      (url) => audioPlayback.setAudioUrl(url),
      resolveLiveStreamingLocale(
        currentUser?.profile?.defaultInputLanguages,
        currentUser?.profile?.language
      ),
      {
        audioSourceMode: audioSourceMode,
        audioInputDeviceId: audioInputDeviceId || undefined,
        sessionId: sessionIdForCapture || undefined,
        preacquiredDisplayStream: presolvedDisplayCapture?.usedMicOnly
          ? undefined
          : presolvedDisplayCapture?.displayStream ?? preacquiredDisplayStream ?? undefined,
        presolvedDisplayCapture: presolvedDisplayCapture || undefined,
        ...(serverLiveSttEnabled && streamingUserId
          ? {
            azureStreaming: {
              userId: String(streamingUserId),
              token: authToken || "",
            },
          }
          : {}),
      }
    );

    setLiveTranscribed(true);
    setMode("transcribing");
  };

  const handlePauseResumeRecording = useCallback(() => {
    if (isResumingCapture || isParkingCapture) return;

    if (recording.isParked) {
      const locale = resolveLiveStreamingLocale(
        currentUser?.profile?.defaultInputLanguages,
        currentUser?.profile?.language
      );
      const sid = activeSession ? String(activeSession) : null;
      setIsResumingCapture(true);
      void (async () => {
        try {
          await recording.resumeParkedRecording(
            () => { },
            (url) => audioPlayback.setAudioUrl(url),
            locale,
            buildLiveCaptureOptions()
          );
          if (sid) {
            invalidateParkedPersist(sid);
            clearParkedCapture(sid);
          }
          setMode("transcribing");
          toast.success("Capture resumed", { duration: 2000, id: "capture-resume" });
        } catch (err) {
          console.warn("Resume parked capture failed:", err?.message || err);
          toast.error("Could not resume capture. Try again.");
        } finally {
          setIsResumingCapture(false);
        }
      })();
      return;
    }

    if (!recording.isRecording) return;
    if (recording.isPaused) {
      recording.resumeRecording();
      toast.success("Capture resumed", { duration: 2000, id: "capture-resume" });
    } else {
      recording.pauseRecording();
      // Persist current full transcript off the critical path.
      const sid =
        activeSessionRef.current ||
        activeSession ||
        (typeof window !== "undefined"
          ? localStorage.getItem("activeSessionId")
          : null);
      const fullText = (recording.getFullTranscriptionText() || "").trim();
      if (sid && fullText) {
        const key = String(sid);
        const idx = transcriptSegmentIndexRef.current[key] ?? 0;
        void audioService
          .saveTempTranscriptSegment(key, {
            text: fullText,
            fullText,
            segmentIndex: idx,
          })
          .then(() => {
            transcriptSegmentIndexRef.current[key] = idx + 1;
          })
          .catch((err) => {
            console.warn("Failed to save paused transcript:", err?.message || err);
          });
      }
      toast("Paused — switch visits anytime; this capture stays ready to resume.", {
        icon: "⏸",
        duration: 2800,
        id: "capture-pause",
      });
    }
  }, [
    recording,
    currentUser,
    buildLiveCaptureOptions,
    audioPlayback,
    activeSession,
    isResumingCapture,
    isParkingCapture,
    invalidateParkedPersist,
  ]);

  // Grammar Functions
  const handleApplySuggestion = (suggestion) => {
    grammarCheck.applySuggestion(
      suggestion,
      recording.getLiveUiSnapshot?.()?.transcript ?? recording.transcript,
      recording.setTranscript
    );
  };

  const handleApplyAllCorrections = () => {
    grammarCheck.applyAllCorrections(recording.setTranscript);
  };

  const resolveSessionIdForApi = useCallback(() => {
    return (
      activeSessionRef.current ||
      activeSession ||
      searchParams.get("sessionId") ||
      (typeof window !== "undefined"
        ? localStorage.getItem("activeSessionId")
        : null)
    );
  }, [activeSession, searchParams]);

  const waitForSessionIdForApi = useCallback(
    async (maxAttempts = 6, delayMs = 400) => {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const id = resolveSessionIdForApi();
        if (id && id !== "undefined" && id !== "null") return id;
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
      return null;
    },
    [resolveSessionIdForApi]
  );

  const extractSoapNoteFromGenerateResponse = (response) => {
    const root = response?.data ?? response;
    const ok =
      root?.statusCode === true ||
      root?.statusCode === 200 ||
      root?.statusCode === "200";
    const data =
      root?.body?.data ??
      root?.body?.body?.data ??
      root?.data?.data ??
      root?.data;
    const soap = data?.soapNote ?? data?.data?.soapNote;
    const message =
      root?.body?.message ?? root?.message ?? "";
    return { ok, soapNote: soap, message };
  };

  /**
   * Toggle the HPI depth for the current (already-generated) chart.
   * - If the requested level was generated before, swap instantly from the
   *   cached variant (no loading, no AI call).
   * - Otherwise regenerate only the HPI/Subjective at the new level, showing a
   *   loading skeleton on the Subjective section, then update live.
   */
  const handleRegenerateHpi = async (requestedLevel) => {
    const lvl = parseInt(requestedLevel, 10);
    if (!(lvl >= 1 && lvl <= 3)) return;
    if (!soapNote?.soapNoteId) return;

    // ★ DEMO MODE: simulate HPI regeneration without backend call
    if (soapNote.soapNoteId === "demo-soap-note" || isDemoMode) {
      if (isProPlusLocked) {
        toast.error(
          "HPI Concise & Detailed modes are available on Professional and Elite. Upgrade to unlock."
        );
        return;
      }

      const currentLevel =
        typeof soapNote?.metadata?.hpiDepthLevel === "number"
          ? soapNote.metadata.hpiDepthLevel
          : 3;
      if (lvl === currentLevel) return;

      // Simulate loading
      setHpiRegenerating(true);

      // Simulate a short delay
      setTimeout(() => {
        // ★ FIX: Use the hpiVariants from demoData and replace the entire HPI block
        const variants = soapNote.metadata?.hpiVariants || {};
        const newHpi =
          variants[lvl] ||
          variants[String(lvl)] ||
          `**HPI (Level ${lvl}):** (No variant defined)`;

        // Update the SOAP note's subjective section and HPI content
        setSoapNote((prev) => {
          if (!prev) return prev;

          const subjective = prev.structuredSections?.subjective || prev.subjective || "";
          
          // Find and replace the entire HPI block (heading + all paragraphs)
          const hpiRegex = /(\*\*HPI[^\n]*:)([\s\S]*?)(?=\n\*\*[A-Z]|$)/;
          const match = subjective.match(hpiRegex);
          
          let newSubjective = subjective;
          if (match) {
            // Replace the whole matched block with the new HPI text
            newSubjective = subjective.replace(match[0], newHpi + "\n\n");
          } else {
            // Fallback: insert after CC section if HPI not found
            const ccRegex = /(\*\*CC[^\n]*:)([\s\S]*?)(?=\n\*\*[A-Z]|$)/;
            const ccMatch = subjective.match(ccRegex);
            if (ccMatch) {
              const before = subjective.substring(0, ccMatch.index + ccMatch[0].length);
              const after = subjective.substring(ccMatch.index + ccMatch[0].length);
              newSubjective = before + "\n\n" + newHpi + "\n\n" + after;
            } else {
              // Just prepend
              newSubjective = newHpi + "\n\n" + subjective;
            }
          }

          const updated = {
            ...prev,
            structuredSections: {
              ...(prev.structuredSections || {}),
              subjective: newSubjective,
              historyOfPresentIllness: newHpi,
            },
            metadata: {
              ...(prev.metadata || {}),
              hpiDepthLevel: lvl,
            },
          };
          updated.subjective = updated.structuredSections.subjective;
          return updated;
        });

        setChartHpiLevel(lvl);
        toast.success(
          lvl === 1
            ? "Switched to Concise HPI (demo)"
            : lvl === 2
              ? "Switched to Balanced HPI (demo)"
              : "Switched to Detailed HPI (demo)",
        );
        setHpiRegenerating(false);
      }, 800);

      return;
    }

    // ---- Real (non-demo) logic remains unchanged ----
    if (isProPlusLocked) {
      toast.error(
        "HPI Concise & Detailed modes are available on Professional and Elite. Upgrade to unlock."
      );
      return;
    }

    const currentLevel =
      typeof soapNote?.metadata?.hpiDepthLevel === "number"
        ? soapNote.metadata.hpiDepthLevel
        : 3;
    if (lvl === currentLevel) return;

    try {
      setHpiRegenerating(true);
      const response = await aiService.regenerateHpi(soapNote.soapNoteId, lvl);
      const { ok, soapNote: updated, message } =
        extractSoapNoteFromGenerateResponse(response);
      if (ok && updated) {
        setSoapNote(updated);
        setChartHpiLevel(lvl);
        toast.success(
          lvl === 1
            ? "Switched to Concise HPI"
            : lvl === 2
              ? "Switched to Balanced HPI"
              : "Switched to Detailed HPI",
        );
      } else {
        toast.error(message || "Failed to update HPI");
      }
    } catch (error) {
      const serverMsg =
        error.response?.data?.body?.message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to update HPI";
      toast.error(serverMsg);
    } finally {
      setHpiRegenerating(false);
    }
  };

  /** Merge regenerated SOAP into existing note so UI state (patient, assignment, etc.) stays intact. */
  const mergeRegeneratedSoapNote = useCallback((prev, updated) => {
    if (!updated) return prev;
    const sections = updated.structuredSections || {};
    const subjective =
      updated.subjective ?? sections.subjective ?? prev?.subjective ?? "";
    const objective =
      updated.objective ?? sections.objective ?? prev?.objective ?? "";
    const assessment =
      updated.assessment ?? sections.assessment ?? prev?.assessment ?? "";
    const plan =
      updated.plan ?? sections.plan ?? prev?.plan ?? "";
    const nextVersion =
      typeof updated.version === "number"
        ? updated.version
        : (prev?.version || 1) + 1;

    return {
      ...prev,
      ...updated,
      soapNoteId: updated.soapNoteId || prev?.soapNoteId,
      sessionId: updated.sessionId || prev?.sessionId,
      subjective,
      objective,
      assessment,
      plan,
      structuredSections: {
        ...(prev?.structuredSections || {}),
        ...(sections || {}),
        subjective,
        objective,
        assessment,
        plan,
        chiefComplaint:
          updated.chiefComplaint ??
          sections.chiefComplaint ??
          prev?.structuredSections?.chiefComplaint,
        historyOfPresentIllness:
          updated.historyOfPresentIllness ??
          sections.historyOfPresentIllness ??
          prev?.structuredSections?.historyOfPresentIllness,
        reviewOfSystems:
          updated.reviewOfSystems ??
          sections.reviewOfSystems ??
          prev?.structuredSections?.reviewOfSystems,
        pastMedicalHistory:
          updated.pastMedicalHistory ??
          sections.pastMedicalHistory ??
          prev?.structuredSections?.pastMedicalHistory,
        medications:
          updated.medications ??
          sections.medications ??
          prev?.structuredSections?.medications,
        allergies:
          updated.allergies ??
          sections.allergies ??
          prev?.structuredSections?.allergies,
        socialHistory:
          updated.socialHistory ??
          sections.socialHistory ??
          prev?.structuredSections?.socialHistory,
        familyHistory:
          updated.familyHistory ??
          sections.familyHistory ??
          prev?.structuredSections?.familyHistory,
        physicalExamination:
          updated.physicalExamination ??
          sections.physicalExamination ??
          prev?.structuredSections?.physicalExamination,
        vitalSigns:
          updated.vitalSigns ??
          sections.vitalSigns ??
          prev?.structuredSections?.vitalSigns,
        diagnosticResults:
          updated.diagnosticResults ??
          sections.diagnosticResults ??
          prev?.structuredSections?.diagnosticResults,
      },
      templateId: updated.templateId ?? prev?.templateId,
      metadata: {
        ...(prev?.metadata || {}),
        ...(updated.metadata || {}),
      },
      version: nextVersion,
    };
  }, []);

  /**
   * After a SOAP note exists for this session, switching the active template
   * regenerates from transcript via the API.
   * Latest-wins: rapid switches don't leave the UI stuck on an older request.
   */
  const handleRegenerateWithTemplate = useCallback(
    async (templateInstanceId, meta = {}) => {
      const currentNote = soapNoteRef.current;
      if (!currentNote?.soapNoteId || !templateInstanceId) return;

      // Demo visit: no real soapNoteId to regenerate against on the backend —
      // simulate the same skeleton-loading UX instead.
      if (isDemoMode || currentNote.soapNoteId === "demo-soap-note") {
        handleDemoTemplateSwitch(templateInstanceId, meta);
        return;
      }

      const currentTemplateId =
        currentNote.metadata?.templateInstanceId ||
        currentNote.templateId ||
        null;
      // Skip only when already on this template AND not mid-regen for another request.
      if (
        currentTemplateId &&
        currentTemplateId === templateInstanceId &&
        !templateRegenInFlightRef.current
      ) {
        return;
      }

      const requestId = ++templateRegenRequestIdRef.current;
      templateRegenInFlightRef.current = true;
      const templateNameHint = meta.templateName || "";

      // Optimistic: point note metadata at the new template immediately.
      const optimistic = {
        ...currentNote,
        templateId: templateInstanceId,
        metadata: {
          ...(currentNote.metadata || {}),
          templateInstanceId,
          ...(templateNameHint
            ? { templateName: templateNameHint }
            : {}),
        },
      };
      soapNoteRef.current = optimistic;
      setSoapNote(optimistic);

      setError(null);
      setActiveTab("soap-note");
      setTemplateRegenLabel(templateNameHint);
      setTemplateRegenerating(true);
      notifyTemplateRegenStatus({
        status: "start",
        instanceId: templateInstanceId,
        templateName: templateNameHint || undefined,
      });

      if (templateRegenTimeoutRef.current) {
        clearTimeout(templateRegenTimeoutRef.current);
      }
      // Safety net so skeletons never stick forever if the request hangs.
      templateRegenTimeoutRef.current = setTimeout(() => {
        if (templateRegenRequestIdRef.current !== requestId) return;
        templateRegenInFlightRef.current = false;
        setTemplateRegenerating(false);
        setTemplateRegenLabel("");
        notifyTemplateRegenStatus({
          status: "error",
          instanceId: templateInstanceId,
          message: "timeout",
        });
        toast.error(
          "Template update is taking longer than expected. Please try again.",
        );
      }, 4 * 60 * 1000);

      try {
        const hasAccess = await canAccessFeature();
        if (templateRegenRequestIdRef.current !== requestId) return;
        if (!hasAccess) {
          toast.error(
            "Trial exhausted. Please subscribe to continue regenerating SOAP notes.",
          );
          return;
        }

        const response = await aiService.regenerateWithTemplate(
          currentNote.soapNoteId,
          templateInstanceId,
        );
        // Ignore stale responses from an older switch.
        if (templateRegenRequestIdRef.current !== requestId) return;

        const root = response?.data ?? response;
        const data =
          root?.body?.data ??
          root?.body?.body?.data ??
          root?.data?.data ??
          root?.data;
        const { ok, soapNote: updated, message } =
          extractSoapNoteFromGenerateResponse(response);

        if (ok && updated) {
          const baseNote = soapNoteRef.current || currentNote;
          const merged = mergeRegeneratedSoapNote(baseNote, updated);
          soapNoteRef.current = merged;
          setSoapNote(merged);
          const templateName =
            data?.templateName ||
            merged?.metadata?.templateName ||
            templateNameHint ||
            "template";
          toast.success(`Updated with "${templateName}"`, { duration: 2200 });
        } else {
          toast.error(message || "Failed to regenerate SOAP with template");
        }
      } catch (error) {
        if (templateRegenRequestIdRef.current !== requestId) return;
        const serverMsg =
          error.response?.data?.body?.message ||
          error.response?.data?.message ||
          error.message ||
          "Failed to regenerate SOAP with template";
        toast.error(serverMsg);
        setError(serverMsg);
      } finally {
        if (templateRegenRequestIdRef.current === requestId) {
          if (templateRegenTimeoutRef.current) {
            clearTimeout(templateRegenTimeoutRef.current);
            templateRegenTimeoutRef.current = null;
          }
          templateRegenInFlightRef.current = false;
          setTemplateRegenerating(false);
          setTemplateRegenLabel("");
          notifyTemplateRegenStatus({
            status: "done",
            instanceId: templateInstanceId,
          });
        }
      }
    },
    [canAccessFeature, mergeRegeneratedSoapNote, setError, isDemoMode, handleDemoTemplateSwitch],
  );

  useEffect(() => {
    const onTemplateChanged = (event) => {
      const instanceId = event?.detail?.instanceId;
      if (!instanceId) return;
      if (!soapNoteRef.current?.soapNoteId) return;
      void handleRegenerateWithTemplate(instanceId, {
        templateName: event?.detail?.templateName || "",
      });
    };
    window.addEventListener(ACTIVE_TEMPLATE_CHANGED_EVENT, onTemplateChanged);
    return () =>
      window.removeEventListener(
        ACTIVE_TEMPLATE_CHANGED_EVENT,
        onTemplateChanged,
      );
  }, [handleRegenerateWithTemplate]);

  const resetSoapGenerationFailure = useCallback(() => {
    soapGenerationFailedRef.current = false;
    setSoapGenerationFailed(false);
    setSoapGenerationFailedAgain(false);
    setShowSoapGenerationFailedModal(false);
  }, []);

  const reportSoapGenerationFailure = useCallback(() => {
    if (soapGenerationFailedRef.current) {
      setSoapGenerationFailedAgain(true);
    }
    soapGenerationFailedRef.current = true;
    setSoapGenerationFailed(true);
    setShowSoapGenerationFailedModal(true);
    setActiveTab("soap-note");
  }, []);

  const applySoapAttemptTranscript = useCallback(
    (transcriptionText, nextTranscriptionId) => {
      const text =
        typeof transcriptionText === "string" ? transcriptionText.trim() : "";
      const tid =
        typeof nextTranscriptionId === "string"
          ? nextTranscriptionId.trim()
          : "";
      if (text) {
        lastSoapAttemptRef.current.transcriptionText = text;
        recording.setTranscript(text);
        if (typeof recording.setFullTranscriptText === "function") {
          recording.setFullTranscriptText(text);
        }
      }
      if (tid) {
        lastSoapAttemptRef.current.transcriptionId = tid;
        setTranscriptionId(tid);
      }
    },
    [recording]
  );

  const handleSoapGenerationFailedFromUpload = useCallback(
    (payload = {}) => {
      if (soapNoteRef.current) return;
      applySoapAttemptTranscript(
        payload.transcriptionText,
        payload.transcriptionId
      );
      setError(null);
      // Already surfaced (including after dismiss). Keep retry data; don't force the popup open again.
      if (soapGenerationFailedRef.current) return;
      reportSoapGenerationFailure();
    },
    [applySoapAttemptTranscript, reportSoapGenerationFailure]
  );

  const handleRegenerateSoapNote = useCallback(() => {
    setShowSoapGenerationFailedModal(false);
    const override = lastSoapAttemptRef.current.transcriptionText || undefined;
    void generateSoapNoteRef.current(override);
  }, []);

  const transcribeStoredAudioThenSoap = useCallback(async (sessionId, localFile) => {
    const sid = sessionId ? String(sessionId) : "";
    if (!sid) {
      throw new Error("No session to transcribe");
    }
    const toastId = "s3-stt-fallback";
    toast.loading("Live transcript was empty. Transcribing the saved recording…", {
      id: toastId,
    });
    try {
      const userId = getAuthUserId();
      if (userId) {
        // Connect in parallel — do not delay enqueueing the job for handshake.
        void ensureWebSocketReady(userId, authToken || undefined);
      }
      let result;
      if (localFile && localFile.size > 0) {
        result = await aiService.transcribeAudioWithSoapAsync(localFile, sid);
      } else {
        result = await aiService.transcribeExistingSession(sid);
      }
      if (result?.jobId) {
        backgroundSoapJobIdsRef.current.set(result.jobId, sid);
      }
      applyLocalSessionProgress(sid, {
        jobId: result?.jobId,
        active: true,
        stage: result?.status === "queued" ? "queued" : "processing",
        percent: result?.status === "queued" ? 0 : 5,
        message:
          result?.status === "queued"
            ? `In queue — transcription will start shortly${result.queuePosition ? ` (position ${result.queuePosition})` : ""}.`
            : "Transcribing the saved recording…",
      });
      if (String(activeSessionRef.current) === String(sid)) {
        setActiveTab("soap-note");
      }
      toast.success("Transcription started from saved audio. SOAP will appear when ready.", {
        id: toastId,
        duration: 5000,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.body?.message ||
        err?.message ||
        "No transcript text available for SOAP generation.";
      toast.error(msg, { id: toastId, duration: 6000 });
      throw err;
    }
  }, [authToken]);

  // Update the generateSoapNote function
  /** @param {string} [transcriptionTextOverride] Pass exact text after post-stop translation (avoids stale React live line). */
  const generateSoapNote = async (transcriptionTextOverride) => {
    if (process.env.REACT_APP_MAINTENANCE_MODE === "true") {
      setShowMaintenanceModal(true);
      return;
    }

    toast.dismiss();
    if (soapNoteRef.current) {
      const sid = activeSessionRef.current;
      if (sid) releaseParkedAfterSoap(sid);
      return;
    }
    if (!isPatientDetailsFilled()) {
      toast.error(
        "Please fill in patient details first before generating SOAP note"
      );
      return;
    }

    const sessionIdForSoap = await waitForSessionIdForApi();
    if (!sessionIdForSoap) {
      toast.error(
        "No active visit session. Wait a moment for the session to load, or refresh the page."
      );
      return;
    }

    invalidateParkedPersist(sessionIdForSoap);

    setShowSoapGenerationFailedModal(false);
    setIsGeneratingSoap(true);
    setIsLoading(true);
    setError(null);
    try {
      const hasAccess = await canAccessFeature();
      if (!hasAccess) {
        toast.error(
          "Trial exhausted. Please subscribe to continue generating SOAP notes."
        );
        return;
      }

      const override =
        typeof transcriptionTextOverride === "string"
          ? transcriptionTextOverride.trim()
          : "";

      let textToUse;
      if (override) {
        textToUse = override;
      } else if (lastSoapAttemptRef.current.transcriptionText) {
        textToUse = lastSoapAttemptRef.current.transcriptionText;
      } else if (liveTranscribed === true) {
        recording.flushPendingLiveLineToAccumulated();
        recording.flushLiveTranscriptFromPending();
        textToUse = (recording.getFullTranscriptionText() || "").trim();
      } else {
        textToUse = (
          grammarCheck.correctedTranscript ||
          recording.transcript ||
          lastSoapAttemptRef.current.transcriptionText ||
          ""
        ).trim();
      }

      const tid = (
        (typeof transcriptionId === "string" && transcriptionId.trim()) ||
        lastSoapAttemptRef.current.transcriptionId ||
        ""
      ).trim();
      if (!textToUse && !tid) {
        try {
          await transcribeStoredAudioThenSoap(sessionIdForSoap, lastLiveAudioFileRef.current);
        } catch (_) {
          /* toast already shown */
        }
        return;
      }

      const transcriptionIdForApi = tid || uuidv4();
      lastSoapAttemptRef.current = {
        transcriptionText: textToUse || lastSoapAttemptRef.current.transcriptionText || "",
        transcriptionId: transcriptionIdForApi,
      };
      if (transcriptionIdForApi !== transcriptionId) {
        setTranscriptionId(transcriptionIdForApi);
      }

      const response = await aiService.generateSoapNote(
        textToUse || "",
        transcriptionIdForApi,
        patientContext,
        sessionIdForSoap,
        { hpiDepthLevel: chartHpiLevel }
      );

      const { ok, soapNote: soapPayload, message } =
        extractSoapNoteFromGenerateResponse(response);

      if (ok && soapPayload) {
        resetSoapGenerationFailure();
        setSoapNote(soapPayload);
        setActiveTab("soap-note");
        releaseParkedAfterSoap(sessionIdForSoap);
        toast.success("SOAP note generated successfully!");
        refreshTrialStatus();
      } else {
        console.error(
          "SOAP note not found in response.",
          message,
          response?.data ?? response
        );
        reportSoapGenerationFailure();
      }
    } catch (error) {
      console.error("SOAP generation error:", error);
      reportSoapGenerationFailure();
    } finally {
      setIsLoading(false);
      setIsGeneratingSoap(false);
    }
  };

  generateSoapNoteRef.current = generateSoapNote;

  /** Debounce before auto-SOAP after upload transcription completes (not live stop — that awaits drain). */
  const AUTO_SOAP_AFTER_STOP_MS = 700;

  /**
   * Queue live-recording SOAP on the same per-user job queue as uploads.
   * Returns after 202 so another visit can start while this chart generates.
   */
  const enqueueLiveSoapGeneration = useCallback(
    async (transcriptionText, sessionId, snapshots = {}) => {
      if (process.env.REACT_APP_MAINTENANCE_MODE === "true") {
        setShowMaintenanceModal(true);
        return;
      }

      const sid = sessionId ? String(sessionId) : "";
      if (!sid) {
        toast.error(
          "No active visit session. Wait a moment for the session to load, or refresh the page."
        );
        return;
      }

      const detailsFilled =
        snapshots.patientDetailsFilled != null
          ? snapshots.patientDetailsFilled
          : isPatientDetailsFilled();
      if (!detailsFilled) {
        toast.error(
          "Please fill in patient details first before generating SOAP note"
        );
        return;
      }

      const stillOnSameSession =
        activeSessionRef.current != null &&
        String(activeSessionRef.current) === String(sid);
      if (stillOnSameSession && soapNoteRef.current) {
        releaseParkedAfterSoap(sid);
        return;
      }

      const hasAccess = await canAccessFeature();
      if (!hasAccess) {
        toast.error(
          "Trial exhausted. Please subscribe to continue generating SOAP notes."
        );
        return;
      }

      const text = (
        typeof transcriptionText === "string" ? transcriptionText : ""
      ).trim();
      if (!text) {
        await transcribeStoredAudioThenSoap(sid, lastLiveAudioFileRef.current);
        return;
      }

      const snapshotTid =
        typeof snapshots.transcriptionId === "string"
          ? snapshots.transcriptionId.trim()
          : "";
      const tid =
        (
          snapshotTid ||
          (typeof transcriptionId === "string" && transcriptionId.trim()) ||
          lastSoapAttemptRef.current.transcriptionId ||
          ""
        ).trim() || uuidv4();

      lastSoapAttemptRef.current = {
        transcriptionText: text,
        transcriptionId: tid,
      };
      if (stillOnSameSession && tid !== transcriptionId) {
        setTranscriptionId(tid);
      }

      invalidateParkedPersist(sid);
      setShowSoapGenerationFailedModal(false);
      setError(null);

      applyLocalSessionProgress(sid, {
        active: true,
        stage: "queued",
        percent: 0,
        message: "Starting SOAP note generation…",
      });
      if (
        stillOnSameSession ||
        String(activeSessionRef.current) === String(sid)
      ) {
        setActiveTab("soap-note");
      }

      const userId = getAuthUserId();
      if (userId) {
        // Connect in parallel — do not delay SOAP enqueue for handshake.
        void ensureWebSocketReady(userId, authToken || undefined);
      }

      try {
        const result = await aiService.generateSoapNoteAsync(
          text,
          tid,
          snapshots.patientContext || patientContext,
          sid,
          { hpiDepthLevel: snapshots.hpiDepthLevel ?? chartHpiLevel }
        );
        if (result.jobId) {
          backgroundSoapJobIdsRef.current.set(result.jobId, sid);
        }
        const queued = result.status === "queued";
        applyLocalSessionProgress(sid, {
          jobId: result.jobId,
          active: true,
          stage: queued ? "queued" : "generating_soap",
          percent: queued ? 0 : 10,
          message: queued
            ? `In queue — SOAP note will start shortly${
                result.queuePosition ? ` (position ${result.queuePosition})` : ""
              }.`
            : "Drafting your SOAP note…",
          queuePosition: result.queuePosition,
        });
        toast.success(
          "SOAP note is generating in the background. You can start another visit.",
          { id: "live-stop-finalize", duration: 4000 }
        );
      } catch (error) {
        console.error("Live SOAP enqueue error:", error);
        applyLocalSessionProgress(sid, {
          active: false,
          stage: "failed",
          percent: 0,
          message: "Failed to start SOAP note generation",
        });
        if (String(activeSessionRef.current) === String(sid)) {
          applySoapAttemptTranscript(text, tid);
          reportSoapGenerationFailure();
        } else {
          toast.error(
            "Failed to start SOAP note generation for the previous visit."
          );
        }
      }
    },
    [
      applySoapAttemptTranscript,
      authToken,
      canAccessFeature,
      chartHpiLevel,
      isPatientDetailsFilled,
      patientContext,
      releaseParkedAfterSoap,
      reportSoapGenerationFailure,
      transcribeStoredAudioThenSoap,
      transcriptionId,
    ]
  );

  const finalizeAndGenerateSoapFromTempTranscript = useCallback(
    async (sessionId, snapshots = {}) => {
      const sid = sessionId ? String(sessionId) : null;
      const snapshotText =
        typeof snapshots.transcriptText === "string"
          ? snapshots.transcriptText.trim()
          : "";
      const stillOnSid =
        sid && String(activeSessionRef.current) === String(sid);
      const canTouchLiveUi =
        stillOnSid && !recording.isRecording && !recording.isParked;
      const liveText = canTouchLiveUi
        ? (recording.getFullTranscriptionText() || "").trim()
        : "";
      let localText = snapshotText;
      if (liveText && liveText.length >= snapshotText.length) {
        localText = liveText;
      }
      if (!localText) localText = liveText || snapshotText;

      let mergedText = localText;
      if (sid) {
        try {
          // Persist final in-memory text, then merge all segments into transcription.txt
          if (localText) {
            const idx = transcriptSegmentIndexRef.current[sid] ?? 0;
            await audioService.saveTempTranscriptSegment(sid, {
              text: localText,
              fullText: localText,
              segmentIndex: idx,
            });
            transcriptSegmentIndexRef.current[sid] = idx + 1;
          }

          const merged = await audioService.mergeTempTranscript(sid, "");
          const fromDisk = String(merged?.data?.text || "").trim();
          if (fromDisk) {
            mergedText = fromDisk.length >= localText.length ? fromDisk : localText;
          }
        } catch (err) {
          console.warn(
            "Temp transcript merge failed; using in-memory transcript:",
            err?.message || err
          );
        }
      }

      if (mergedText) {
        const stillOnSidNow =
          sid && String(activeSessionRef.current) === String(sid);
        if (
          stillOnSidNow &&
          !recording.isRecording &&
          !recording.isParked
        ) {
          recording.setFullTranscriptText(mergedText);
        }
        await enqueueLiveSoapGeneration(mergedText, sid, snapshots);
      } else {
        try {
          await transcribeStoredAudioThenSoap(sid, lastLiveAudioFileRef.current);
        } catch (_) {
          /* toast already shown */
        }
      }
      if (sid) {
        releaseParkedAfterSoap(sid);
      }
    },
    [enqueueLiveSoapGeneration, recording, transcribeStoredAudioThenSoap, releaseParkedAfterSoap]
  );

  /**
   * On stop: if pause/park chunks exist, upload only the final new segment,
   * then merge all chunks into one live S3 file. Otherwise upload the local
   * merged file directly as the single live recording.
   */
  const finalizeMergedLiveAudioUpload = useCallback(
    async (sessionId, localMergedFile, meta = {}) => {
      const sid = sessionId ? String(sessionId) : null;
      if (!sid) return null;

      const hasLocal =
        localMergedFile && typeof localMergedFile.size === "number" && localMergedFile.size > 0;
      if (hasLocal) {
        lastLiveAudioFileRef.current = localMergedFile;
        registerSessionAudio(sid, localMergedFile);
        try {
          audioPlayback.setAudioUrl(URL.createObjectURL(localMergedFile));
        } catch (_) { /* ignore */ }
      }

      await waitForParkedPersist(sid);

      let chunkCount = audioChunkIndexRef.current[sid] ?? 0;
      try {
        const state = await audioService.getTempAudioState(sid);
        chunkCount = Math.max(chunkCount, Number(state?.data?.chunkCount) || 0);
      } catch (_) { /* optional */ }

      const finalSegment = meta?.finalSegment;

      try {
        // Per-session merge: add any new final segment, then combine THIS session's chunks.
        if (finalSegment && finalSegment.size > 0) {
          const mime = finalSegment.type || "audio/webm";
          const ext = mime.includes("webm") ? "webm" : "wav";
          const file = new File([finalSegment], `live_seg_${chunkCount}.${ext}`, {
            type: mime,
          });
          await audioService.uploadTempAudioChunk(sid, file, chunkCount, {
            isFinal: true,
          });
          chunkCount += 1;
          audioChunkIndexRef.current[sid] = chunkCount;
        }

        if (chunkCount > 0) {
          const combined = await audioService.combineTempAndUploadLive(sid);
          if (combined?.success) {
            registerSessionAudio(sid, {
              ...(combined.data || {}),
              filename: combined.data?.filename || `live_recording_${sid}.webm`,
              size: combined.data?.size,
              contentType: combined.data?.contentType || "audio/webm",
              uploadStatus: combined.data?.uploadStatus || "ready",
              s3Key: combined.data?.s3Key,
            });
            void triggerAudioFetch();
            return combined;
          }
        }

        if (hasLocal) {
          const response = await audioService.uploadLiveAudio(sid, localMergedFile);
          if (response?.success) {
            registerSessionAudio(sid, {
              ...(response.data || {}),
              filename: localMergedFile.name,
              size: localMergedFile.size,
              contentType: localMergedFile.type,
              uploadStatus: response.data?.uploadStatus || "ready",
              s3Key: response.data?.s3Key,
            });
            void triggerAudioFetch();
          }
          return response;
        }
      } catch (error) {
        console.warn("Merged live audio upload failed:", error);
        if (hasLocal) {
          try {
            return await audioService.uploadLiveAudio(sid, localMergedFile);
          } catch (e2) {
            console.warn("Direct live audio upload also failed:", e2);
          }
        }
      }
      return null;
    },
    [audioPlayback, registerSessionAudio, triggerAudioFetch, waitForParkedPersist]
  );

  const handleStopTranscribing = () => {
    const sessionIdForUpload = resolveSessionIdForApi();
    const wasParked = recording.isParked && !recording.isRecording;
    const parkedSnapshot = sessionIdForUpload
      ? getParkedCapture(sessionIdForUpload)
      : null;
    const liveSoapSnapshots = {
      patientContext: { ...patientContext },
      hpiDepthLevel: chartHpiLevel,
      transcriptionId,
      patientDetailsFilled: isPatientDetailsFilled(),
      transcriptText: (recording.getFullTranscriptionText() || "").trim(),
    };
    const generationAtClick =
      typeof recording.getCaptureGeneration === "function"
        ? recording.getCaptureGeneration()
        : null;
    liveStopSessionIdRef.current = String(
      sessionIdForUpload || activeSessionRef.current || "pending"
    );
    if (sessionIdForUpload) {
      invalidateParkedPersist(sessionIdForUpload);
      markCaptureFinished(sessionIdForUpload);
      clearParkedCapture(sessionIdForUpload);
    }
    recording.endParkedCapture();
    if (typeof recording.releaseCaptureUi === "function") {
      recording.releaseCaptureUi();
    }
    setMode(null);
    setShowTranscriptionInSoapTab(true);

    if (sessionIdForUpload) {
      // Show WS/job status immediately on Stop — do not wait for drain + enqueue.
      applyLocalSessionProgress(sessionIdForUpload, {
        active: true,
        stage: "finalizing",
        percent: 8,
        message: "Finalizing live capture…",
      });
    }

    if (wasParked && parkedSnapshot) {
      const snapSegs = Array.isArray(parkedSnapshot.audioSegments)
        ? parkedSnapshot.audioSegments
        : [];
      const hookSegs = recording.getParkedSnapshot()?.audioSegments || [];
      const hookHasAudio = hookSegs.some((b) => b && b.size > 0);
      const storeHasAudio = snapSegs.some((b) => b && b.size > 0);
      if (storeHasAudio && !hookHasAudio) {
        recording.applyParkedSnapshot(parkedSnapshot);
        recording.endParkedCapture();
        if (typeof recording.releaseCaptureUi === "function") {
          recording.releaseCaptureUi();
        }
      }
    }

    const toastId = "live-stop-finalize";
    toast.success("Recording saved. SOAP note is generating in the background.", {
      id: toastId,
      duration: 3500,
    });

    const uploadThenFinishParked = async (file, url, meta) => {
      await finalizeMergedLiveAudioUpload(sessionIdForUpload, file, meta);
      if (sessionIdForUpload) {
        invalidateParkedPersist(sessionIdForUpload);
        markCaptureFinished(sessionIdForUpload);
        clearParkedCapture(sessionIdForUpload);
      }
    };

    const stopOpts =
      generationAtClick != null ? { generation: generationAtClick } : undefined;
    const stopPromise = recording.stopTranscribing(
      () => {},
      uploadThenFinishParked,
      stopOpts
    );

    void (async () => {
      try {
        const drained = await stopPromise;
        if (typeof drained === "string" && drained.trim()) {
          liveSoapSnapshots.transcriptText = drained.trim();
        }
        await finalizeAndGenerateSoapFromTempTranscript(
          sessionIdForUpload,
          liveSoapSnapshots
        );
      } catch (error) {
        console.error("Stop transcribing failed:", error);
        const liveText = (recording.getFullTranscriptionText?.() || "").trim();
        const savedText =
          lastSoapAttemptRef.current.transcriptionText ||
          liveSoapSnapshots.transcriptText ||
          "";
        const hasTranscript = Boolean(liveText || savedText);
        if (hasTranscript) {
          applySoapAttemptTranscript(liveText || savedText, transcriptionId);
          reportSoapGenerationFailure();
        } else {
          toast.error("Failed to stop transcribing and generate SOAP note. Please try again.");
        }
        if (sessionIdForUpload) {
          invalidateParkedPersist(sessionIdForUpload);
          markCaptureFinished(sessionIdForUpload);
          clearParkedCapture(sessionIdForUpload);
        }
      } finally {
        const stoppedSid = liveStopSessionIdRef.current;
        liveStopSessionIdRef.current = null;
        const stillOnStoppedVisit =
          stoppedSid &&
          stoppedSid !== "pending" &&
          String(activeSessionRef.current) === String(stoppedSid);
        if (!stillOnStoppedVisit) {
          recording.endParkedCapture();
        }
      }
    })();
  };

  const handleTranscriptionComplete = async (
    transcript,
    corrected = transcript,
    jobSessionId = null,
    soapNoteId = null,
    patientName = null,
    jobId = null
  ) => {
    const currentSession = activeSessionRef.current;

    let resolvedSessionId = jobSessionId;
    let resolvedSoapNoteId = soapNoteId;

    if (jobId) {
      try {
        const jr = await aiService.getTranscriptionJobById(jobId);
        const payload = jr?.data ?? jr;
        const job =
          payload?.body?.data?.job ??
          payload?.body?.data ??
          payload?.data?.job;
        if (job && typeof job === "object") {
          if (job.sessionId != null && String(job.sessionId).trim() !== "") {
            resolvedSessionId = job.sessionId;
          }
          const soapFromJobResult = job.result?.soapNoteId;
          if (soapFromJobResult) {
            resolvedSoapNoteId = resolvedSoapNoteId || soapFromJobResult;
          }
        }
      } catch (e) {
        console.warn("Transcription job hydrate failed (non-fatal):", e?.message || e);
      }
    }

    const trackedSessionId = jobId
      ? backgroundSoapJobIdsRef.current.get(jobId)
      : null;
    const wasBackgroundSoapJob = Boolean(trackedSessionId);
    if (jobId) {
      backgroundSoapJobIdsRef.current.delete(jobId);
    }

    if (
      (resolvedSessionId == null || String(resolvedSessionId).trim() === "") &&
      trackedSessionId
    ) {
      resolvedSessionId = trackedSessionId;
    }

    const isCurrentSession =
      resolvedSessionId != null &&
      String(resolvedSessionId).trim() !== "" &&
      currentSession != null &&
      String(resolvedSessionId) === String(currentSession);

    // If the completed job belongs to a different session than the one open, don't switch tabs.
    if (!isCurrentSession) {
      if (resolvedSessionId) {
        applyLocalSessionProgress(resolvedSessionId, {
          jobId,
          active: false,
          stage: "completed",
          percent: 100,
          message: "Your SOAP note is ready.",
        });
        releaseParkedAfterSoap(resolvedSessionId, { endActiveParked: false });
      }
      refreshTrialStatus();
      const otherSessionToastKey = `other:${String(resolvedSessionId || "unknown")}:${String(
        resolvedSoapNoteId || jobId || "unknown"
      )}`;
      showCompletionToastOnce(
        otherSessionToastKey,
        `SOAP note generated for ${patientName || "patient"}.`,
        { duration: 7000 }
      );
      return;
    }

    const sessionForSoap =
      resolvedSessionId != null && String(resolvedSessionId).trim() !== ""
        ? resolvedSessionId
        : currentSession;

    if (isPatientDetailsFilled()) {
      recording.setTranscript(transcript);
      grammarCheck.setCorrectedTranscript(corrected);
    } else {
      toast.error("Please fill in patient details first");
    }

    const switchToSoapTab = () => {
      const fn = handleTabChangeRef.current;
      if (typeof fn === "function") {
        void fn("soap-note");
      } else {
        setActiveTab("soap-note");
      }
    };

    try {
      if (resolvedSoapNoteId) {
        const notePayload = await fetchSoapNoteByIdPayload(
          resolvedSoapNoteId,
          sessionForSoap
        );
        if (notePayload) {
          flushSync(() => {
            setSoapNote(notePayload);
          });
          applyLocalSessionProgress(sessionForSoap, {
            active: false,
            stage: "completed",
            percent: 100,
            message: "Your SOAP note is ready.",
          });
          refreshTrialStatus();
          releaseParkedAfterSoap(sessionForSoap);
          switchToSoapTab();
          const sameSessionToastKey = `current:${String(sessionForSoap || "unknown")}:${String(
            resolvedSoapNoteId || jobId || notePayload?.soapNoteId || "unknown"
          )}`;
          showCompletionToastOnce(
            sameSessionToastKey,
            patientName
              ? `SOAP note ready for ${patientName}`
              : "SOAP note is ready — open the SOAP Note tab to review.",
            { duration: 6000 }
          );
          return;
        }
      }

      if (sessionForSoap) {
        const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const attempts = 15;
        const baseDelayMs = 500;

        for (let attempt = 0; attempt < attempts; attempt++) {
          const hasSoap = await loadSoapNotesForSession(sessionForSoap, true);
          if (hasSoap) {
            applyLocalSessionProgress(sessionForSoap, {
              active: false,
              stage: "completed",
              percent: 100,
              message: "Your SOAP note is ready.",
            });
            refreshTrialStatus();
            releaseParkedAfterSoap(sessionForSoap);
            switchToSoapTab();
            const sameSessionToastKey = `current:${String(sessionForSoap || "unknown")}:${String(
              resolvedSoapNoteId || jobId || "from-session-poll"
            )}`;
            showCompletionToastOnce(
              sameSessionToastKey,
              patientName
                ? `SOAP note ready for ${patientName}`
                : "SOAP note is ready — open the SOAP Note tab to review.",
              { duration: 6000 }
            );
            return;
          }

          if (attempt < attempts - 1) {
            await sleep(baseDelayMs * (attempt + 1));
          }
        }
      }
    } catch (e) {
      console.warn("Failed to refresh SOAP note after transcription:", e);
    }

    if (wasBackgroundSoapJob) {
      applyLocalSessionProgress(sessionForSoap || resolvedSessionId, {
        active: false,
        stage: "failed",
        percent: 0,
        message: "SOAP generation failed",
      });
      if (transcript) {
        applySoapAttemptTranscript(transcript, null);
      }
      reportSoapGenerationFailure();
      return;
    }

    setActiveTab("transcription");
    window.setTimeout(() => {
      void generateSoapNoteRef.current();
    }, AUTO_SOAP_AFTER_STOP_MS);
  };

  // Update patient context function
  const updatePatientContext = async (data) => {
    try {
      const updatedPatientContext = { ...patientContext, ...data };
      setPatientContext(updatedPatientContext);
      const currentSessionId = activeSessionRef.current || activeSession;
      const patientIsComplete = Boolean(
        String(updatedPatientContext?.name || `${updatedPatientContext?.firstName || ""} ${updatedPatientContext?.lastName || ""}`).trim(),
      ) && Boolean(updatedPatientContext?.visitType);

      // Autosave is also allowed to create the first real visit. The ref is
      // used so a following manual Save in the same tick reuses this visit.
      if (!currentSessionId && !isAssignedSOAPNote && patientIsComplete) {
        await handlePatientInfoSave(updatedPatientContext);
        return;
      }

      // If we have an active session and patient name exists, update session name
      if (currentSessionId && updatedPatientContext.name && !isAssignedSOAPNote) {
        await updateSessionName(currentSessionId, updatedPatientContext.name);
      }

      // Save to session (only for regular sessions)
      if (currentSessionId && !isAssignedSOAPNote) {
        await sessionService.updateSession(currentSessionId, {
          patient: updatedPatientContext,
        });
        emitSessionPatch({                      // ← new
          op: "upsert",
          sessionId: currentSessionId,
          patch: { patient: updatedPatientContext },
        });
      }
      setTimeout(() => {
        setSessionsVersion(v => v + 1);
      }, 200);
    } catch (error) {
      console.error("Failed to save patient data:", error);
      // toast.error("Failed to save patient data");
    }
  };

  // Update the handlePatientInfoSave function
  const handlePatientInfoSave = async (submittedPatientContext = null) => {
    const patientToSave = submittedPatientContext || patientContext;
    const hasPatientDetails = Boolean(
      patientToSave?.name?.trim?.() ||
      `${patientToSave?.firstName || ""} ${patientToSave?.lastName || ""}`.trim(),
    ) && Boolean(
      typeof patientToSave?.visitType === "string"
        ? patientToSave.visitType.trim()
        : patientToSave?.visitType,
    );
    if (hasPatientDetails) {
      try {
        let sessionId = activeSessionRef.current || activeSession;

        // If no session exists and not in assigned mode, create one
        if (!sessionId && !isAssignedSOAPNote) {
          const createRes = await sessionService.createSession({
            sessionName: patientToSave?.name || `Visit ${new Date().toLocaleDateString()}`,
            encounterType: "consultation",
            specialty: "general",
          });
          const created =
            createRes?.data?.session ||
            createRes?.data?.data?.session ||
            createRes?.body?.session ||
            createRes?.body?.data?.session ||
            (createRes?.data?.data?.sessionId != null ? createRes.data.data : null) ||
            (createRes?.body?.data?.sessionId != null ? createRes.body.data : null) ||
            (createRes?.data?.sessionId != null ? createRes.data : null) ||
            (createRes?.body?.sessionId != null ? createRes.body : null) ||
            (createRes?.sessionId != null ? createRes : null);
          const newId =
            created?.sessionId ??
            created?.id ??
            createRes?.data?.sessionId ??
            createRes?.body?.sessionId ??
            createRes?.sessionId;
          if (newId != null && newId !== "") {
            sessionId = String(newId);
            activeSessionRef.current = sessionId;
            setActiveSession(sessionId);
            setSearchParams({ sessionId }, { replace: true });
            /* setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("sessionId", sessionId);
                return next;
              },
              { replace: true },
            ); */
            localStorage.setItem("activeSessionId", sessionId);
            localStorage.setItem("lastSessionId", sessionId);
            const createdSessionRow = {
              id: sessionId,
              sessionId,
              name: created?.sessionName || patientToSave?.name || "Untitled Session",
              sessionName: created?.sessionName || patientToSave?.name || "Untitled Session",
              createdAt: created?.createdAt || new Date().toISOString(),
              visitDate: created?.visitDate || new Date().toISOString(),
              status: created?.status || "active",
              patient: patientToSave,
            };
            setSessions((prev) =>
              sortSessionsNewestFirst([
                createdSessionRow,
                ...prev.filter((item) => String(item.id) !== sessionId),
              ]),
            );
            emitSessionPatch({
              op: "add",
              session: createdSessionRow,
            });
          } else {
            throw new Error("Failed to create new session");
          }
        }

        // Save patient data to the session (only for regular sessions)
        if (sessionId && !isAssignedSOAPNote) {
          const response = await sessionService.updateSession(sessionId, {
            patient: patientToSave,
          });

          if (response.success) {
            setActiveSession(sessionId);
            localStorage.setItem("activeSessionId", sessionId);
            localStorage.setItem("lastSessionId", sessionId);
            setSearchParams({ sessionId }, { replace: true });
            /* setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("sessionId", sessionId);
                return next;
              },
              { replace: true },
            ); */
            emitSessionPatch({
              op: "upsert",
              sessionId,
              patch: {
                patient: patientToSave,
                name: patientToSave?.name || undefined,
                sessionName: patientToSave?.name || undefined,
              },
            });
            setShowPrechartingSection(true);
          } else {
            toast.error(
              response.message || "Failed to save patient information"
            );
          }
        } else if (isAssignedSOAPNote) {
          toast.success("Patient information updated!");
        }
      } catch (error) {
        console.error("Failed to save patient data:", error);
        toast.error("Failed to save patient information. Please try again.");
      }
    } else {
      console.warn("At least one patient detail is required before saving.");
    }
  };

  // Clear Session
  function clearSession(options = {}) {
    const { skipRecordingClear = false, keepVisitSelected = false } = options;
    if (!skipRecordingClear) {
      recording.clearRecording();
    }
    audioPlayback.clearAudio();
    grammarCheck.clearGrammarCheck();
    setTranscriptionId("");
    setSoapNote(null);
    setPatientContext({});
    setError(null);
    lastSoapAttemptRef.current = { transcriptionText: "", transcriptionId: "" };
    resetSoapGenerationFailure();
    setUploadTranscriptionStream({ active: false, lines: [], status: "" });
    setActiveTab("patient");
    setShowTranscriptionInSoapTab(false);
    setIsDemoMode(false);
    setIsViewingDemoSnapshot(false);
    demoTourStartedRef.current = false;
    if (!keepVisitSelected) {
      setAwaitingVisitSelection(true);
    }
  }

  // Main-panel skeleton only (SessionManager has its own list skeleton).

  const MainContentSkeleton = ({ message }) => (
    <div
      className="absolute inset-0 z-[60] flex flex-col bg-gradient-to-b from-slate-50 to-blue-50 p-4 sm:p-6 gap-4 overflow-hidden"
      aria-busy="true"
      aria-label="Loading"
    >
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500 shrink-0">
        <DottedLoader size="sm" />
        <span>{message}</span>
      </div>
      <div className="h-12 w-full max-w-3xl rounded-xl bg-white border border-slate-200 animate-pulse shrink-0" />
      <div className="flex gap-2 shrink-0">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-9 w-24 rounded-lg bg-slate-200 animate-pulse" />
        ))}
      </div>
      <div className="flex-1 min-h-0 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4 animate-pulse overflow-hidden">
        <div className="h-5 w-1/3 bg-slate-200 rounded-md" />
        <div className="h-3 w-full bg-slate-100 rounded-md" />
        <div className="h-3 w-5/6 bg-slate-100 rounded-md" />
        <div className="h-3 w-4/5 bg-slate-100 rounded-md" />
        <div className="h-40 sm:h-52 w-full bg-slate-100 rounded-xl mt-2" />
        <div className="h-3 w-2/3 bg-slate-100 rounded-md" />
        <div className="h-3 w-3/4 bg-slate-100 rounded-md" />
        <div className="h-3 w-1/2 bg-slate-100 rounded-md" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <div className="h-28 bg-slate-100 rounded-xl" />
          <div className="h-28 bg-slate-100 rounded-xl" />
        </div>
      </div>
    </div>
  );

  // Check if we are on the exact scribe index route
  const isScribeIndex = location.pathname === "/scribe";

  return (
    <div className="flex h-[100dvh] min-h-[100svh] w-full min-w-0 flex-col overflow-hidden bg-gradient-to-b from-slate-50 to-blue-50 sm:bg-gradient-to-br">
      {/* Demo Tour (Joyride) — separate component, only renders/does anything once runTour is true */}
      <DemoTour
        key={tourKey}
        run={demoTour.runTour}
        onCallback={handleJoyrideCallback}
        stepIndex={demoTour.runTour ? tourStepIndex : undefined}
        showSkip={tourShowSkip}
        isDemoCapturing={
          isDemoMode && (recording.isRecording || demoBackgroundProgress.active)
        }
        // Only intercept Next on Capture while the demo note doesn't exist yet.
        // Back from SOAP → Next (note exists) and restarted tours use the
        // normal Joyride advance.
        onDemoCaptureNext={
          isDemoMode && !soapNote ? handleDemoCaptureNext : undefined
        }
      />

      {/* Welcome Modal */}
      {showWelcomeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div
            className={`bg-white rounded-2xl shadow-2xl p-6 relative animate-in fade-in zoom-in duration-300 ${
              welcomeStep === 1 ? "max-w-3xl" : "max-w-lg"
            } w-full`}
          >
            {/* Step 1: Get Started / Welcome */}
            {welcomeStep === 1 && (
              <div className="transition-all duration-300">
                {/* Logo / Brand */}
                <div className="flex justify-center mb-6">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="w-8 h-8 text-blue-600" />
                    <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                      VirtualScribe
                    </span>
                  </div>
                </div>

                {/* Main Content */}
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    Your documentation
                    <br />
                    just got faster
                  </h2>
                  {/* Video section – larger, unmuted, lazy load */}
                  <div className="mb-4">
                    <WelcomeVideoPlayer />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={() => setWelcomeStep(2)}
                    className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl font-medium hover:shadow-lg hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    Get started
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Everything you need */}
            {welcomeStep === 2 && (
              <div className="transition-all duration-300">
                {/* Icon */}
                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-blue-100 to-teal-100 rounded-2xl flex items-center justify-center">
                    <Mic className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                  </div>
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-2 px-1 leading-snug">
                    Everything you need to prepare, document, and follow up
                    faster
                  </h2>
                </div>

                {/* Feature Cards */}
                <div className="space-y-2.5 sm:space-y-4 mb-4 sm:mb-6">
                  <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-800 text-base sm:text-lg mb-0.5 sm:mb-1">
                        Prep
                      </h4>
                      <p className="text-sm sm:text-lg text-gray-600 leading-snug sm:leading-relaxed">
                        Review synthesized patient summaries and get
                        evidence-based medical intelligence
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-800 text-base sm:text-lg mb-0.5 sm:mb-1">
                        Conduct
                      </h4>
                      <p className="text-sm sm:text-lg text-gray-600 leading-snug sm:leading-relaxed">
                        Run your visits like normal, we will handle the
                        documentation
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-green-50 rounded-xl border border-green-100">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-gray-800 text-base sm:text-lg mb-0.5 sm:mb-1">
                        Complete
                      </h4>
                      <p className="text-sm sm:text-lg text-gray-600 leading-snug sm:leading-relaxed">
                        Your note, codes, and documents are instantly generated
                        for review.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <button
                    onClick={handleStartDemo}
                    className="col-span-2 py-3 sm:py-3.5 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl font-medium text-lg sm:text-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    Start a demo visit
                  </button>
                  <button
                    onClick={handleStartVisit}
                    className="col-span-2 py-2.5 sm:py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium text-lg sm:text-xl hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <User size={16} className="sm:hidden" />
                    <User size={18} className="hidden sm:block" />
                    Start a live visit instead
                  </button>
                </div>

                {/* Footer Info */}
                <div className="mt-4 sm:mt-5 flex items-center justify-center gap-2 sm:gap-4 text-base sm:text-lg text-gray-400">
                  <span className="flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-500" />
                    Interactive tour
                  </span>
                  <span className="w-px h-3 bg-gray-200" />
                  <span className="flex items-center gap-1">
                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
                    No setup needed
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Microphone permission denied modal (demo visit) */}
      {micDeniedMessage && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center animate-in fade-in zoom-in duration-300">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Microphone access needed
            </h3>
            <p className="text-gray-500 text-sm mb-5">
              Please enable microphone access in your browser settings to try
              the demo visit.
            </p>
            <button
              onClick={() => setMicDeniedMessage(false)}
              className="w-full py-2.5 bg-purple-600 text-white rounded-xl font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Demo visit completion modal */}
      {showDemoComplete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-20 h-20 mx-auto mb-5 bg-gradient-to-br from-blue-100 to-teal-100 rounded-2xl flex items-center justify-center">
              <Stethoscope className="w-10 h-10 text-blue-600" />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              🎉 You're all set!
            </h2>

            <p className="text-gray-600 text-xl leading-8 mb-8">
              You've completed the demo visit and learned how VirtualScribe
              works.
            </p>
            <button
              onClick={handleExitDemoStartRealVisit}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-teal-700 hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            >
              Start Your First Visit
            </button>
          </div>
        </div>
      )}

      {/* GLOBAL HEADER (mobile) */}
      {isMobile && (
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
          <div className="flex items-center h-14 lg:h-16 px-4 sm:px-6">
            {/* Mobile Menu */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>

            {/* Logo */}
            <div className="flex items-center ml-2">
              <img
                src={logo}
                alt="VirtualScribe"
                className="w-auto h-11 sm:h-9 object-contain cursor-pointer"
              />
            </div>
          </div>
        </header>
      )}

      {/* SIDEBAR + MAIN CONTENT – horizontal row */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-row overflow-hidden">
        {/*Sidebar*/}
        <Profiler id="SessionManager" onRender={onRenderProfiler}>
          <SessionManager
            activeSession={activeSession}
            isDemoActive={isDemoMode}
            demoSessionEntry={demoSessionEntry}
            onSelectDemoVisit={handleSelectDemoVisit}
            onDeleteDemoVisit={handleDeleteDemoVisit}
            isRecordingActive={false}
            parkedSessionIds={parkedSessionIds}
            isParkingCapture={isParkingCapture}
            onCreateSession={createNewSession}
            onSelectSession={selectSession}
            onDeleteSession={deleteSession}
            onUpdateSessionName={updateSessionName}
            isCreatingSession={isCreatingSession}
            mobileMenuOpen={mobileMenuOpen}
            mobileSection={mobileSection}
            setMobileMenuOpen={setMobileMenuOpen}
            setMobileSection={setMobileSection}
            sessionsVersion={sessionsVersion}
            sessionListPatch={sessionListPatch}
            newVisitButtonRef={newVisitButtonRef}
            keepVisitsPanelOpen={
              isDemoMode || demoTour.runTour || keepTourPanelOpen
            }
            onRestartTutorial={handleRestartTutorial}
            className={`w-full sm:w-64 md:w-72 lg:w-80 border-b sm:border-b-0 sm:border-r border-slate-200 bg-white/80 backdrop-blur-sm ${mobileMenuOpen ? "z-60" : ""}`}
          />
        </Profiler>

        {/* Main content area */}
        <div
          ref={mainContentColumnRef}
          className="relative z-30 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        >
          {isSwitchingSession && !isLoadingSessions && (
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-30 h-0.5 overflow-hidden bg-blue-100/80"
              aria-hidden="true"
            >
              <div className="h-full w-1/3 animate-pulse rounded-full bg-gradient-to-r from-blue-500 via-teal-500 to-blue-500" />
            </div>
          )}
          {isLoadingSessions && !isScribeIndex && (
            <MainContentSkeleton
              message={
                isParkingCapture ? "Saving capture…" : "Loading session…"
              }
            />
          )}

          {/* ---- MODIFIED: Conditional rendering based on isScribeIndex ---- */}
          {isScribeIndex ? (
            <div
              className={`flex-1 flex flex-col relative min-h-0 min-w-0 w-full transition-opacity duration-200 ease-out ${
                isSwitchingSession ? "opacity-70" : "opacity-100"
              }`}
            >
              {/* Header*/}
              <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className="flex flex-col h-full"
              >
                {/* <ActiveTemplateIndicator variant="bar" /> */}
                <Profiler id="SessionHeader" onRender={onRenderProfiler}>
                  <SessionHeaderWithLiveClock
                    recording={recording}
                    isRecording={recording.isRecording || recording.isParked}
                    isPaused={recording.isPaused || recording.isParked}
                    isParked={recording.isParked}
                    isResumingCapture={isResumingCapture}
                    onPauseResumeRecording={handlePauseResumeRecording}
                    transcriptionSource={recording.transcriptionSource}
                    isUploadTranscriptionStreaming={
                      uploadTranscriptionStream.active
                    }
                    onToggleRecording={
                      recording.isRecording || recording.isParked
                        ? isDemoMode
                          ? handleStopDemoEarly
                          : handleStopTranscribing
                        : handleStartTranscribing
                    }
                    onClearSession={clearSession}
                    mode={mode}
                    setMode={setMode}
                    activeSessionName={activeSessionName}
                    onUpdateSessionName={(newName) =>
                      updateSessionName(activeSession, newName)
                    }
                    mobileMenuOpen={mobileMenuOpen}
                    mobileSection={mobileSection}
                    setMobileMenuOpen={setMobileMenuOpen}
                    patientContext={patientContext}
                    isPatientDetailsFilled={isPatientDetailsFilled()}
                    soapNote={soapNote}
                    activeTab={activeTab}
                    aiService={aiService} // pass the aiService instance
                    onUpdateSoapNote={async (updatedNote) => {
                      setSoapNote(updatedNote); // update state in parent
                    }}
                    handleTabChange={handleTabChange}
                    isAssignedSOAPNote={isAssignedSOAPNote}
                    isAdminView={isAssignedSOAPNote}
                    onUpdatePatient={updatePatientContext}
                    onShowTranscriptionInSoapTab={
                      handleShowTranscriptionInSoapTab
                    }
                    hpiLevel={chartHpiLevel}
                    onHpiLevelChange={setChartHpiLevel}
                    onRegenerateHpi={handleRegenerateHpi}
                    hpiRegenerating={hpiRegenerating}
                    audioSourceMode={audioSourceMode}
                    onAudioSourceModeChange={setAudioSourceMode}
                    audioInputDeviceId={audioInputDeviceId}
                    onAudioInputDeviceChange={setAudioInputDeviceId}
                    getAudioInputDevices={recording.getAudioInputDevices}
                    onPreRequestMicPermission={recording.checkMicPermission}
                    onWarmLiveWebSocket={() => {
                      const uid =
                        currentUser?.userId ??
                        currentUser?.userid ??
                        currentUser?.id ??
                        null;
                      if (!uid) return;
                      void ensureWebSocketReady(
                        String(uid),
                        authToken || "",
                        12000,
                      );
                    }}
                    isProcessing={isGeneratingSoap}
                    isSessionLoading={isSessionDataLoading}
                    onRestartTutorial={handleRestartTutorial}
                    tourTemplateOpen={demoTour.runTour && tourTemplateOpen}
                    onTourTemplateOpenChange={setTourTemplateOpen}
                    tourRecordingOpen={demoTour.runTour && tourRecordingOpen}
                    onTourRecordingOpenChange={setTourRecordingOpen}
                    isTourCaptureStep={
                      demoTour.runTour && tourStepIndex === TOUR_STEP.CAPTURE
                    }
                  />
                </Profiler>

                <div 
                 ref={soapScrollRef}
                 className="flex-1 px-4 pb-10 sm:px-6 overflow-y-auto overflow-x-auto main-content-scroll-container soap-tab-scroll-container"
                 >
                  {(!isAssignedSOAPNote || isAdmin) && (
                    <TabsContent
                      value="patient"
                      forceMount
                      className="mt-0 data-[state=inactive]:hidden"
                    >
                      <PatientContextForm
                        context={patientContext}
                        updateContext={updatePatientContext}
                        onSave={handlePatientInfoSave}
                        isDemoMode={isDemoMode}
                        demoAnimationNonce={demoAnimationNonce}
                        isTourPatientStep={
                          demoTour.runTour && activeTourStep === TOUR_STEP.PATIENT
                        }
                      />
                      {isPatientDetailsFilled() && (
                        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 text-green-700">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm font-medium">
                              Patient details saved. You can now start capturing
                              audio.
                            </span>
                          </div>
                        </div>
                      )}
                      {isPatientDetailsFilled() && (
                        <div className="mt-6 w-full min-w-0 flex">
                          <button
                            type="button"
                            onClick={() =>
                              setShowPrechartingSection((prev) => !prev)
                            }
                            className="
                            inline-flex items-center justify-center gap-1.5
                            w-full sm:w-auto max-w-full min-w-0
                            px-4 py-2.5
                            rounded-full border border-blue-300 bg-white
                            text-blue-600 hover:bg-blue-50 hover:text-blue-700
                            transition-colors
                            text-sm font-medium
                            text-center
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                            "
                          >
                            {showPrechartingSection
                              ? "Hide optional pre-charting"
                              : "Add optional pre-charting"}
                          </button>
                        </div>
                      )}
                      {isPatientDetailsFilled() && showPrechartingSection && (
                        <div className="mt-4">
                          <PrechartingTab
                            sessionId={activeSession}
                            patientContext={patientContext}
                            onDataChange={handlePrechartingDataChange}
                            readonly={!!soapNote}
                          />
                        </div>
                      )}
                    </TabsContent>
                  )}

                  <TabsContent
                    value="upload"
                    forceMount
                    className="mt-0 data-[state=inactive]:hidden"
                  >
                    {/* Patient Details Required Notice */}
                    {!isPatientDetailsFilled() && (
                      <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-center gap-2 text-orange-700">
                          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                          <span className="text-sm font-medium">
                            Please fill in patient details first before
                            uploading audio.
                          </span>
                        </div>
                      </div>
                    )}

                    <AudioUploadTab
                      activeTab={activeTab}
                      isSessionLoading={isSessionDataLoading}
                      onTranscriptionComplete={handleTranscriptionComplete}
                      onLoadingChange={setIsLoading}
                      setError={setError}
                      mode={mode}
                      setMode={setMode}
                      sessionId={activeSession}
                      setTranscriptionId={setTranscriptionId}
                      setLiveTranscribed={setLiveTranscribed}
                      setShowMaintenanceModal={setShowMaintenanceModal}
                      onAudioUploaded={(sid, file) => {
                        if (sid) registerSessionAudio(sid, file || undefined);
                        void triggerAudioFetch();
                      }}
                      onUploadTranscriptionStreamState={
                        onUploadTranscriptionStreamState
                      }
                      onUploadTranscriptionStart={onUploadTranscriptionStart}
                      onProgressChange={(payload) => {
                        const sid = payload?.sessionId;
                        if (!sid) return;
                        applyLocalSessionProgress(sid, payload);
                      }}
                      onSoapGenerationFailed={
                        handleSoapGenerationFailedFromUpload
                      }
                    />

                    {error && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                        {error}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent
                    value="soap-note"
                    forceMount
                    className="mt-0 data-[state=inactive]:hidden"
                  >
                    <div>
                      {soapNote ? (
                        <>
                          <div
                            className={
                              showSoapRevealEffect
                                ? "animate-in fade-in duration-300"
                                : ""
                            }
                          >
                            <EnhancedSoapNoteDisplay
                              soapNote={soapNote}
                              patientContext={patientContext}
                              aiService={aiService}
                              isAssignedSOAPNote={isAssignedSOAPNote}
                              isAdminView={isAssignedSOAPNote}
                              activeSession={activeSession}
                              onSoapNoteUpdate={handleSoapNoteUpdate}
                              onSaveSuccess={() => {
                                toast.success("SOAP note updated successfully");
                              }}
                              typingAnimationNonce={soapTypingAnimationNonce}
                              transcriptionPanel={transcriptionPanel}
                              hpiRegenerating={hpiRegenerating}
                              templateRegenerating={templateRegenerating}
                              templateRegenLabel={templateRegenLabel}
                              isDemoMode={isDemoMode}
                              onDemoIcdChange={handleDemoIcdChange}
                              onDemoIcdAdd={handleDemoIcdAdd}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          {transcriptionPanel}
                          <Card className="bg-white/80 backdrop-blur-sm border-slate-200 shadow-lg">
                            <CardContent className="text-center py-12">
                              <SoapNotePlaceholderCard
                                sessionId={activeSession}
                                isDemoMode={isDemoMode}
                                demoBackgroundProgress={demoBackgroundProgress}
                                isAssignedSOAPNote={isAssignedSOAPNote}
                                soapGenerationFailed={soapGenerationFailed}
                                soapGenerationFailedAgain={
                                  soapGenerationFailedAgain
                                }
                                isGeneratingSoap={isGeneratingSoap}
                                onRegenerateSoapNote={handleRegenerateSoapNote}
                              />
                            </CardContent>
                          </Card>
                        </>
                      )}
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          ) : (
            // For all other routes (dashboard, templates, settings, analytics, etc.)
            // render the Outlet (the nested route component)
            <div className="flex-1 h-full min-h-0 overflow-y-auto">
              <Outlet />
            </div>
          )}
        </div>
      </div>

      {/* Trial Exhausted Modal */}
      <TrialExhaustedModal
        isOpen={showTrialExhaustedModal}
        onClose={handleTrialExhaustedModalClose}
        onSubscribe={navigateToPayment}
        onViewPricing={navigateToPricing}
        trialInfo={{
          remainingTrials: trialStatus.remainingTrials,
          totalTrials: trialStatus.totalTrials,
          usedTrials: trialStatus.usedTrials,
        }}
      />

      {/* SOAP generation technical failure */}
      <SoapGenerationFailedModal
        isOpen={showSoapGenerationFailedModal && !isGeneratingSoap}
        onClose={() => setShowSoapGenerationFailedModal(false)}
        onRegenerate={handleRegenerateSoapNote}
        isRegenerating={isGeneratingSoap}
        failedAgain={soapGenerationFailedAgain}
      />

      {/* Full-screen SOAP Generation Loader */}
      {isGeneratingSoap && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
                <div className="w-16 h-16 border-4 border-transparent border-t-blue-600 rounded-full animate-spin absolute top-0 left-0"></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {soapGenerationFailed
                  ? "Regenerating your SOAP note"
                  : "Drafting your SOAP note"}
              </h3>
              <p className="text-gray-600 text-lg">
                This usually takes about a minute…
              </p>
            </div>
            <div className="space-y-2 text-sm text-gray-500">
              <p>• Turning your transcript into clinical sections</p>
              <p>• Highlighting problems, exam findings, and assessment</p>
              <p>• Writing Subjective, Objective, Assessment & Plan</p>
              <p>• Almost ready for review</p>
            </div>
          </div>
        </div>
      )}
      {showMaintenanceModal && (
        <MaintenanceModal
          isOpen={showMaintenanceModal}
          onClose={() => setShowMaintenanceModal(false)}
          onNavigateToSettings={handleNavigateToSettings}
          onNavigateToSupport={handleNavigateToSupport}
        />
      )}
      {recording.RecordingModalsGate()}
    </div>
  );
}